import React, { useMemo } from "react";
import { fetchQuery, graphql, useFragment } from "react-relay";
import { Link, useLocation } from "react-router-dom";

import {
	faCommentAltLines,
	faCopy,
	faDeleteRight as faDeleteRightLight,
	faLink,
	faMinus,
	faSearch,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faAngleDown,
	faAngleUp,
	faArrowsFromDottedLine,
	faDeleteRight as faDeleteRightRegular,
	faDesktop,
	faEllipsisV,
	faExclamationCircle,
	faLanguage,
	faUserCircle,
	faWaveform,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faDeleteRight as faDeleteRightSolid,
	faPause,
	faPlay,
	faStop,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { autoUpdate, flip, inline, offset, shift, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import uFuzzy from "@leeoniya/ufuzzy";
import * as Sentry from "@sentry/react";
import cx from "classnames";
import { animate } from "framer-motion";
import { debounce, memoize, noop } from "lodash";

import { BillingContext } from "~/account/Billing/BillingContext";
import { AppContext } from "~/common";
import { TRANSCRIPT_TAB } from "~/constants/meetingRecorderTabs";
import { ReactComponent as ScreenShareStopIcon } from "~/legacy-assets/images/icons/stop-screenshare.svg";
import { urlsafeB64Encode } from "~/lib/base64";
import { useDebouncedValue, useID } from "~/lib/hooks";
import { confirm } from "~/lib/prompts";
import { mergeRefs } from "~/lib/refs";
import { commitMutationPromise, useMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { THREE_PER_EM_SPACE } from "~/lib/unicode";
import {
	getFirstUserFacingError,
	isSameFloatNumber,
	nGetText,
	plural,
	preventDefault,
	TIME_COMPARISON_TOLERANCE,
} from "~/lib/utils";
import TranscriptCommentsPopover from "~/meetingRecorder/TranscriptCommentsPopover";
import { environment } from "~/models";
import { Button, Checkbox, RadioGroup, toast, Tooltip, useModals } from "~/ui";
import Avatar, { type AvatarUserAttrs } from "~/ui/avatar";
import { SimpleUserList } from "~/ui/avatar/SimpleUserList";
import { IconButton } from "~/ui/button/IconButton";
import { SimpleInput } from "~/ui/Input";
import BetaLabel from "~/ui/labels/BetaLabel";
import { ButtonLink, HelpCenterIcon, Modal, TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import { __globalModalContext, type ModalProps } from "~/ui/modals";
import AsyncCreatableSelector from "~/ui/selectors/AsyncCreatableSelector";
import CreatableSelector from "~/ui/selectors/CreatableSelector";
import { outlinedStyles } from "~/ui/selectors/SelectorUtils";

import { showFeatureModal, UpgradeIcon } from "../billing";
import { getLanguageInformationForCode, languageCodeIsSupported, languageIsEnglish } from "./languageUtils";
import RedactingTranscriptHeader from "./Redaction/RedactingTranscriptHeader";
import { RedactionMarker } from "./Redaction/RedactionMarker";
import RegenerateForLanguageModal from "./RegenerateForLanguageModal";
import type { TranscriptMode } from "./TranscriptModal";
import { TranscriptModalContext } from "./TranscriptModalContext";
import {
	copyToClipboard,
	formatTimestamp,
	getTimestampFromLocation,
	numHourDigitsForDuration,
	useGetURL,
	useIsPublic,
} from "./utils";
import { keyEventsFragment } from "./Video/Timeline";

import type { Timeline_meetingKeyEvents$key } from "~/__generated__/Timeline_meetingKeyEvents.graphql";
import type { DeletionReason } from "~/__generated__/TranscriptModal_meetingRecording.graphql";
import type { TranscriptPanel_DismissLowLanguageConfidenceRegenerationBannerMutation as DismissRegenerationBannerMutation } from "~/__generated__/TranscriptPanel_DismissLowLanguageConfidenceRegenerationBannerMutation.graphql";
import type { TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording$key } from "~/__generated__/TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording.graphql";
import type { TranscriptPanel_makeSpeakerNameToUserMapping_meetingRecording$key } from "~/__generated__/TranscriptPanel_makeSpeakerNameToUserMapping_meetingRecording.graphql";
import type { TranscriptPanel_note$key } from "~/__generated__/TranscriptPanel_note.graphql";
import type { TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording$key } from "~/__generated__/TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording.graphql";
import type { TranscriptPanel_SpeakerRelabelForm_attendees$key } from "~/__generated__/TranscriptPanel_SpeakerRelabelForm_attendees.graphql";
import type {
	TranscriptPanel_Transcript_meetingRecording$data,
	TranscriptPanel_Transcript_meetingRecording$key,
} from "~/__generated__/TranscriptPanel_Transcript_meetingRecording.graphql";
import type { TranscriptPanel_Transcript_viewer$key } from "~/__generated__/TranscriptPanel_Transcript_viewer.graphql";
import type { TranscriptPanel_TranscriptSegment_meetingRecording$key } from "~/__generated__/TranscriptPanel_TranscriptSegment_meetingRecording.graphql";
import type { TranscriptPanel_TranscriptSegment_note$key } from "~/__generated__/TranscriptPanel_TranscriptSegment_note.graphql";
import type { TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording$key } from "~/__generated__/TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording.graphql";
import type { TranscriptPanelRelabelMultipleSpeakersMutation } from "~/__generated__/TranscriptPanelRelabelMultipleSpeakersMutation.graphql";
import type { TranscriptPanelWorkspaceUsersQuery } from "~/__generated__/TranscriptPanelWorkspaceUsersQuery.graphql";

import styles from "./css/TranscriptPanel.module.css";

interface TranscriptWord {
	text: string;
	start_timestamp: number;
	end_timestamp: number;
}

enum TranscriptSegmentType {
	speech,
	keyEvent,
}

export interface SpeakerTranscriptSegment {
	words: TranscriptWord[];
	speaker: string;
	speaker_known?: boolean;
	auto_labeled?: boolean;
	segmentType: TranscriptSegmentType;
	// This option allows us to render a component rather than just words
	reactNode?: React.ReactNode;
	keyEventIcon?: React.ReactNode;
}

export interface KeyEvent {
	eventType: string;
	participantName: string | null;
	timeFromRecordingStart: number;
	endTimeFromRecordingStart: number | null;
	duration: number | null;
}

type ScrollPosition = "center" | "top";

interface TranscriptSegmentRef {
	scrollIntoView(position: ScrollPosition): { cancel(): void };
	setActive(active: boolean): void;
}

type SpeakerToUserMapping = {
	[fullName: string]: { fullName: string; email?: string; image?: string; isTopUser?: boolean; user?: { id: string } };
};

type RedactingSpan = [start: number, end: number];

type TranscriptCommentData = NonNullable<TranscriptPanel_Transcript_meetingRecording$data["comments"]>[0];

interface TranscriptSegmentProps {
	meetingRecording: TranscriptPanel_TranscriptSegment_meetingRecording$key;
	note: TranscriptPanel_TranscriptSegment_note$key | null;
	segment: SpeakerTranscriptSegment;
	onClick: (segment: SpeakerTranscriptSegment) => void;
	onClickRedactionStartMarker?: (segment: SpeakerTranscriptSegment) => void;
	onClickRedactionEndMarker?: (segment: SpeakerTranscriptSegment) => void;
	videoDuration: number | null;
	searchMatches?: number[];
	speakerNameToUserMapping: SpeakerToUserMapping;
	sameSpeakerAsPreviousSegment: boolean;
	redactionIsVisible: boolean;
	planHasRedaction: boolean;
	activeRedactions: readonly RedactingSpan[] | null;
	addRedactingSpan: (span: RedactingSpan) => void;
	enterRedactionMode: (analyticsLocation: string) => void;
	currentSpanStart: number | null;
	currentSpanEnd: number | null;
	isClip?: boolean;
	openCommentPanel?: (start: number, end: number) => void;
	selectedCommentTimestampStart: number | null;
	selectedCommentTimestampEnd: number | null;
	isRedactionOpen: boolean;
	isRedactionInProgress: boolean;
	commentData?: TranscriptCommentData[];
	hasAccessToRecapComments: boolean;
}

function getSegmentSpeaker(segment: SpeakerTranscriptSegment): string {
	if (segment.auto_labeled) return `${segment.speaker} (?)`;
	else return segment.speaker;
}

// In this modal we have auto scrolling, which is disabled when you manually
// scroll. We use scroll events to tell that the user is scrolling, but there
// are some challenges:
//
// No matter how you programmatically scroll an element, scroll events are
// triggered (e.g. setting scrollTop, scrollIntoView). Scroll events are
// actually just plain old Events, so there is no way to distinguish between a
// scroll event from programmatically changing the scroll position and an event
// from the user scrolling. A single change of scrollTop can also result in
// multiple scroll events, and the scroll event is only triggered on the tick
// after scrollTop is changed. All of this makes it pretty much impossible to
// correctly ignore only scroll events that are caused by programmatic
// scrolling.
//
// I also tried using "wheel" and "touchmove" events to detect when the user is
// scrolling manually, but these don't necessarily mean the user is scrolling,
// and it's not possible to tell when the user scrolls by moving the scroll bar.
//
// This global variable is used to tell if a scroll event was triggered by the
// user or not. Unfortunately it means we will ignore scroll events by the user
// while animating the scroll position, but it's the best I could come up with.
let autoScrolling = false;

function MarkedText({ text, matches }: { text: string; matches: number[] }) {
	let nextKey = 0;
	const markedText = uFuzzy.highlight(
		text,
		matches,
		(part, matched) =>
			matched ? (
				<mark key={nextKey++} className={styles.searchMatchedText}>
					{part}
				</mark>
			) : (
				<React.Fragment key={nextKey++}>{part}</React.Fragment>
			),
		[] as React.ReactNode[],
		(acc, part) => {
			acc.push(part);
			return acc;
		},
	);
	return <>{markedText}</>;
}

/** For a DOM node, an offset into it, and an ancestor node, adjust the offset to
 * be relative to the ancestor. */
function adjustOffsetRelativeToAncestor(anchor: Node, offset: number, ancestor: Node): number {
	if (!ancestor.contains(anchor)) throw new Error("Ancestor does not contain anchor");
	let adjustedOffset = offset;
	let node: Node | null = anchor;
	while (node && node !== ancestor) {
		if (node.previousSibling) {
			node = node.previousSibling;
			adjustedOffset += Array.from(node.textContent ?? "").length;
		} else {
			node = node.parentNode;
		}
	}
	return adjustedOffset;
}

/** adjustOffsetRelativeToAncestor, backwards. Find the DOM node and offset as deep
 * as possible within the ancestor node with a given text offset. */
function findNodeAndOffsetFromAdjustedOffset(ancestor: Node, offset: number): [Node, number] {
	let adjustedOffset = offset;
	let node: Node = ancestor;
	while (node.hasChildNodes() || adjustedOffset > (node.textContent?.length ?? 0)) {
		if (node.hasChildNodes()) {
			for (const child of node.childNodes) {
				if (child.textContent) {
					if (adjustedOffset <= child.textContent.length) {
						node = child;
						break;
					} else {
						adjustedOffset -= child.textContent.length;
					}
				}
			}
		}
	}
	return [node, adjustedOffset];
}

function CommentPill({ data, onClick }: { data: TranscriptCommentData[]; onClick: (event: React.MouseEvent) => void }) {
	const renderAttendee = (user: TranscriptCommentData["users"][0]) => {
		const attendee = {
			...user,
			user,
		};

		return (
			<div className={styles.commentsAttendee} key={`user-avatar-${user.id}`}>
				<Avatar className={styles.commentAvatar} size="medium" user={attendee} />
			</div>
		);
	};

	const threadCount = data.length;

	if (threadCount === 0) return <></>;

	const onClickHandler = (event: React.MouseEvent) => {
		event.stopPropagation(); // it's necessary to stop playing the video when clicking on the comment pill
		onClick(event);
	};

	const seenUsers = new Set<string>();

	const firstAttendees = data.reduce(
		(acc, curr) => {
			curr.users.slice(0, 4).forEach(user => {
				if (!seenUsers.has(user.id)) {
					acc.push(user);
					seenUsers.add(user.id);
				}
			});
			return acc;
		},
		[] as TranscriptCommentData["users"][0][],
	);

	const othersAttendees = data.reduce(
		(acc, curr) => {
			curr.users.slice(4).forEach(user => {
				if (!seenUsers.has(user.id)) {
					acc.push(user);
					seenUsers.add(user.id);
				}
			});
			return acc;
		},
		[] as TranscriptCommentData["users"][0][],
	);

	const othersLength = othersAttendees.length;

	const othersAvatar = <span>{othersLength}</span>;

	const hasZeroComments = threadCount === 1 && data[0].count === 0;

	if (hasZeroComments) return null;

	return (
		<div className={styles.commentsWrapper} onClick={onClickHandler}>
			<div className={styles.commentsAttendeesWrapper}>
				{othersLength > 0 && (othersLength === 1 ? renderAttendee(othersAttendees[0]) : othersAvatar)}
				{firstAttendees.map(renderAttendee)}
			</div>

			{threadCount > 0 && (
				<div>
					{threadCount === 1
						? `${data[0].count} ${nGetText(data[0].count, "comment", "comments")}`
						: `${threadCount} comment threads`}
				</div>
			)}
		</div>
	);
}

/** Like python's bisect.bisect_left/bisect_right, but for transcripts which have
 * both start and end points. */
function bisectSegmentList(
	segments: readonly SpeakerTranscriptSegment[],
	timestamp: number,
	start = 0,
	end = segments.length,
	round: "left" | "right" = "left",
): number {
	if (start < 0) throw new Error("start must be >= 0");
	if (end > segments.length) throw new Error("end must be <= segments.length");

	let low = start,
		high = end;

	while (low < high) {
		const mid = Math.floor((low + high) / 2);
		const midSegment = segments[mid];
		const midStart = midSegment.words[0].start_timestamp;
		const midEnd = midSegment.words[midSegment.words.length - 1].end_timestamp;
		if (midEnd <= timestamp) low = mid + 1;
		else if (midStart >= timestamp) high = mid;
		else return round === "left" ? mid : mid + 1;
	}
	return low;
}

/** For a given DOM position in a TranscriptSegment, return the start or end timestamp of the word it's in */
function getTimestampFromDOMPosition(
	anchor: Node,
	offset: number,
	transcript: readonly SpeakerTranscriptSegment[],
	side: "start" | "end",
): number | null {
	const anchorElement = anchor instanceof Element ? anchor : anchor.parentElement;
	const segmentContainer = anchorElement?.closest(`.${styles.transcriptSegmentContainer} [data-segment-start]`);
	if (!segmentContainer) return null;
	const segmentStartAttribute = segmentContainer.getAttribute("data-segment-start");
	if (!segmentStartAttribute) return null;
	const segmentStart = parseFloat(segmentStartAttribute);

	const correctedOffset = adjustOffsetRelativeToAncestor(anchor, offset, segmentContainer);

	const segmentIndex = bisectSegmentList(transcript, segmentStart);

	const segment = transcript[segmentIndex];
	const words = segment.words;
	for (let i = 0, charI = 0; i < words.length; i++) {
		if (correctedOffset <= charI + words[i].text.length) {
			return side === "start" ? words[i].start_timestamp : words[i].end_timestamp;
		}
		charI += words[i].text.length + 1;
	}

	return null;
}

/** For a given timestamp in a transcript, return the DOM position of the word it's in */
function getDOMPositionFromTimestamp(
	parentElement: HTMLElement,
	timestamp: number,
	transcript: readonly SpeakerTranscriptSegment[],
	side: "start" | "end",
	/** If we already know the segment containing the timestamp, we can avoid
	 * doing the search again by providing it as an argument */
	segment: SpeakerTranscriptSegment = transcript[bisectSegmentList(transcript, timestamp)],
): [Node, number] {
	const segmentContentElement = parentElement.querySelector(
		`.${styles.transcriptSegmentContainer} [data-segment-start="${segment.words[0].start_timestamp}"]`,
	);
	if (!segmentContentElement) throw new Error("Can't find segment line");

	let wordIndex = -1;
	for (let i = 0; i < segment.words.length; i++) {
		const lowerBound = segment.words[i].start_timestamp;
		const upperBound =
			i + 1 < segment.words.length ? segment.words[i + 1].start_timestamp : segment.words[i].end_timestamp;
		if (timestamp >= lowerBound && timestamp <= upperBound) {
			wordIndex = i;
			break;
		}
	}
	if (wordIndex === -1) throw new Error("Can't find word");
	const adjustedOffset =
		segment.words.slice(0, wordIndex).reduce((acc, word) => acc + word.text.length + 1, 0) +
		(side === "end" ? segment.words[wordIndex].text.length : 0);
	return findNodeAndOffsetFromAdjustedOffset(segmentContentElement, adjustedOffset);
}

/** Works like Python's itertools.group_by, not lodash's groupBy. */
function itertoolsGroupBy<T, K = T>(iterable: Iterable<T>, keyFunc: (item: T) => K): ReadonlyArray<[K, T[]]> {
	const groups = new Array<[K, T[]]>();
	let currentGroup: T[] = [];
	let lastKey: K | null = null;
	for (const item of iterable) {
		const key = keyFunc(item);
		if (lastKey !== null && key !== lastKey) {
			groups.push([lastKey, currentGroup]);
			currentGroup = [];
		}
		currentGroup.push(item);
		lastKey = key;
	}
	if (lastKey !== null) groups.push([lastKey, currentGroup]);
	return groups;
}

export function VideoTimestamp({
	timestamp,
	videoDuration,
	className,
	onClick,
	disabled,
	isInlineTimestamp,
}: {
	/** in seconds */
	timestamp: number;
	videoDuration: number | null;
	className?: string;
	onClick?: (timestamp: number) => void;
	disabled?: boolean;
	isInlineTimestamp?: boolean;
}) {
	const numHourDigits = numHourDigitsForDuration(videoDuration);
	const overAnHour = numHourDigits > 0;

	const onTimestampClick = React.useCallback(() => {
		onClick?.(timestamp);
	}, [onClick, timestamp]);

	return (
		<button
			onClick={onTimestampClick}
			className={cx(
				styles.videoTimestamp,
				{
					[styles.overAnHour]: overAnHour,
					[styles.videoTimestampDisabled]: !onClick,
					[styles.isInlineTimestamp]: isInlineTimestamp,
				},
				className,
			)}
			style={{ "--numHourDigits": numHourDigits.toString() } as React.CSSProperties}
			disabled={disabled}
		>
			{formatTimestamp(timestamp, videoDuration)}
		</button>
	);
}
function isStartWithinSegment(segment: SpeakerTranscriptSegment, start: number) {
	const segmentStart = segment.words[0].start_timestamp;
	const segmentEnd = segment.words.at(-1)?.end_timestamp;
	if (segmentEnd === undefined) return false;
	return start >= segmentStart && start < segmentEnd;
}

/* Different from start so that if two segments start and end at the same time,
 * a redaction which starts at that time will be considered part of the second
 * segment, whereas a redaction which ends at that time will be considered part
 * of the first segment. */
function isEndWithinSegment(segment: SpeakerTranscriptSegment, end: number) {
	const segmentStart = segment.words[0].start_timestamp;
	const segmentEnd = segment.words.at(-1)?.end_timestamp;
	if (segmentEnd === undefined) return false;
	return end > segmentStart && end <= segmentEnd;
}

const TranscriptSegment = React.memo(
	React.forwardRef<TranscriptSegmentRef, TranscriptSegmentProps>(function TranscriptSegment(
		{
			note: noteKey,
			meetingRecording: meetingRecordingKey,
			segment,
			onClick,
			onClickRedactionStartMarker,
			onClickRedactionEndMarker,
			videoDuration,
			searchMatches,
			speakerNameToUserMapping,
			sameSpeakerAsPreviousSegment,
			redactionIsVisible,
			planHasRedaction,
			activeRedactions,
			addRedactingSpan,
			enterRedactionMode,
			currentSpanStart,
			currentSpanEnd,
			isClip,
			openCommentPanel,
			selectedCommentTimestampStart,
			selectedCommentTimestampEnd,
			commentData,
			isRedactionOpen,
			isRedactionInProgress,
			hasAccessToRecapComments,
		},
		ref,
	) {
		const note = useFragment(
			graphql`
				fragment TranscriptPanel_TranscriptSegment_note on Note {
					id
				}
			`,
			noteKey,
		);
		const meetingRecording = useFragment(
			graphql`
				fragment TranscriptPanel_TranscriptSegment_meetingRecording on MeetingRecordingInterface {
					id
					ownerAccount {
						__typename
					}
					...utils_useGetURL_recording
				}
			`,
			meetingRecordingKey,
		);

		const containerRef = React.useRef<HTMLLIElement>(null);
		const transcriptTextRef = React.useRef<HTMLParagraphElement>(null);
		const { analyticsFingerprint } = React.useContext(BillingContext);

		React.useImperativeHandle(ref, () => ({
			setActive(active: boolean) {
				containerRef.current?.classList.toggle(styles.active, active);
			},

			scrollIntoView(position: ScrollPosition = "center") {
				const noopResult = { cancel: noop };

				if (!containerRef.current) return noopResult;
				const thisEl = containerRef.current;

				const listElement = thisEl.parentElement;
				const scrollContainer = listElement?.parentElement;
				if (!scrollContainer) return noopResult;

				// Get the nearest "article" element, which is the parent of the
				const noteEl = listElement.closest("article[data-type='note-content'], [role='dialog'] > main");
				if (!noteEl) return noopResult;

				if (position !== "center") {
					// Since we're going to the top, we can just use the browser's
					// scrollIntoView method.
					thisEl.scrollIntoView({ behavior: "smooth", block: "start" });
					return noopResult;
				}

				const containerBox = scrollContainer.getBoundingClientRect(),
					thisElBox = thisEl.getBoundingClientRect(),
					noteBox = noteEl.getBoundingClientRect();

				// Now let's bound the containerBox to the noteBox and the window.
				let bounds = {
					top: Math.max(containerBox.top, noteBox.top, 0),
					bottom: Math.min(containerBox.bottom, noteBox.bottom, window.innerHeight),
				};

				const dest = Math.max(
					0,
					Math.min(
						thisEl.offsetTop +
							thisElBox.height / 2 + // Target point relative to container
							containerBox.top - // Add to top of container
							(bounds.top + (bounds.bottom - bounds.top) / 2), // Subtract to desired point
					),
				);

				const playback = animate(scrollContainer.scrollTop, dest, {
					duration: 0.2,
					onUpdate(value) {
						autoScrolling = true;
						scrollContainer.scrollTop = value;
					},
				});
				return {
					cancel() {
						playback.stop();
					},
				};
			},
		}));

		const getMeetingRecordingURL = useGetURL(meetingRecording);
		const onCopyClick = React.useCallback(() => {
			if (!getMeetingRecordingURL) return;
			const urlPromise = getMeetingRecordingURL({
				timestamp: segment.words[0].start_timestamp,
				noteId: note?.id ?? undefined,
			});
			copyToClipboard(urlPromise).then(() => {
				dispatchAnalyticsTrack("[AI Copilot] Transcript Link Copied", { fingerprint: analyticsFingerprint });
			});
		}, [note, segment, getMeetingRecordingURL, analyticsFingerprint]);

		const onSegmentClick = React.useCallback(
			(e: React.MouseEvent<HTMLLIElement>) => {
				// Ignore the click event if it came from inside the kebab menu.
				if (e.defaultPrevented) return;

				// Don't activate the click function if holding alt/option,
				// same as `<a>` links
				if (e.altKey) return;

				// Ignore the click event if the user selected some text inside the
				// transcript segment.
				const selection = window.getSelection();
				if (selection?.containsNode(e.currentTarget, true) && String(selection)) {
					return;
				}
				onClick(segment);
			},
			[onClick, segment],
		);

		const onTimestampClick = React.useCallback(() => {
			onClick(segment);
		}, [onClick, segment]);

		const fullText = segment.words.map(word => word.text).join(" ");

		let markedText, speaker: React.ReactNode;
		if (!searchMatches) {
			if (activeRedactions?.length) {
				markedText = itertoolsGroupBy(segment.words, word =>
					activeRedactions.some(([start, end]) => word.start_timestamp >= start && word.end_timestamp <= end),
				).map(([isRedacted, words], i) => (
					<React.Fragment key={words[0].start_timestamp}>
						{i > 0 && " "}
						<span className={cx({ [styles.redactedText]: isRedacted })}>{words.map(word => word.text).join(" ")}</span>
					</React.Fragment>
				));
			} else {
				markedText = <React.Fragment key="text">{fullText}</React.Fragment>;
			}
			speaker = getSegmentSpeaker(segment);
		} else {
			const textMatches = [],
				speakerMatches = [],
				// Plus one because there's a space between the text and the speaker name
				speakerTextOffset = fullText.length + 1;

			// Split matches into those that match the transcript segment and those
			// that match the speaker's name. The speaker's name is appended to end of
			// the segment text for searching, so any matches at indexes >= the length
			// of the text is a match on the speaker's name.
			for (let i = 0; i + 1 < searchMatches.length; i += 2) {
				const start = searchMatches[i],
					end = searchMatches[i + 1];

				// I don't know if it's possible for a match range to overlap multiple
				// words, but we can handle it here fairly easily so why not. If a range
				// overlaps both the transcript text and speaker name, we'll split it
				// into two ranges; one from the match start to the end of the transcript
				// text and the other from the start of the speaker name to the match end.
				if (start < fullText.length) {
					const actualEnd = Math.min(end, fullText.length);
					textMatches.push(start, actualEnd);
				}
				if (end > speakerTextOffset) {
					const actualStart = Math.max(speakerTextOffset, start);
					speakerMatches.push(actualStart - speakerTextOffset, end - speakerTextOffset);
				}
			}

			markedText = <MarkedText text={fullText} matches={searchMatches} />;
			if (speakerMatches.length > 0) {
				speaker = <MarkedText text={getSegmentSpeaker(segment)} matches={speakerMatches} />;
			} else {
				speaker = getSegmentSpeaker(segment);
			}
		}

		const isKeyEvent = segment.segmentType === TranscriptSegmentType.keyEvent;

		const transcriptText = (
			<p
				ref={transcriptTextRef}
				className={cx(styles.transcriptSegmentText, { [styles.keyEventSegmentText]: isKeyEvent })}
				data-segment-start={segment.words[0].start_timestamp}
			>
				{markedText}
			</p>
		);

		const segmentSpeakerUser = speakerNameToUserMapping[segment.speaker];

		const avatarUser: AvatarUserAttrs = React.useMemo(() => {
			const mappingSpeaker = segmentSpeakerUser;
			if (mappingSpeaker) {
				return { ...mappingSpeaker, email: mappingSpeaker.email ?? "" };
			}
			return { email: "", fullName: segment.speaker };
		}, [segment.speaker, segmentSpeakerUser]);

		const isPublic = useIsPublic();
		const isGuest = window.INITIAL_STATE.user?.type === "GUEST";
		const inRedactionMode = activeRedactions !== null;
		const isXWC = meetingRecording.ownerAccount.__typename === "ExternalAccount";

		const relabelingEnabled = useFeatureIsOn("relabeling-and-conference-rooms");
		const preventRelabeling = isPublic || isGuest || isXWC || !relabelingEnabled || isClip || inRedactionMode;

		const onClickRedactionStartMarkerImpl = React.useCallback(() => {
			onClickRedactionStartMarker?.(segment);
		}, [onClickRedactionStartMarker, segment]);

		const onClickRedactionEndMarkerImpl = React.useCallback(() => {
			onClickRedactionEndMarker?.(segment);
		}, [onClickRedactionEndMarker, segment]);

		const openCommentsPanel = React.useCallback(
			(event: React.MouseEvent) => {
				event.preventDefault();

				if (!segment.words || segment.words.length === 0 || !openCommentPanel) return;
				openCommentPanel(segment.words[0].start_timestamp, segment.words[0].end_timestamp);
			},
			[segment, openCommentPanel],
		);

		const [commentFlyoutOpen, setCommentFlyoutOpen] = React.useState(false);
		const offsetProps = { mainAxis: -4, crossAxis: -32 };
		const commentHoverFloating = useFloating({
			open: commentFlyoutOpen,
			onOpenChange: setCommentFlyoutOpen,
			placement: "top-end",
			strategy: "absolute",
			middleware: [offset(offsetProps)],
		});
		const hover = useHover(commentHoverFloating.context);
		const commentHoverInteractions = useInteractions([hover]);

		const shouldShowCommentOption = React.useMemo(() => !isPublic && !isClip && !isXWC, [isXWC, isClip, isPublic]);

		const shouldShowCommentFlyout = React.useMemo(
			() =>
				shouldShowCommentOption && commentFlyoutOpen && !isRedactionOpen && !isRedactionInProgress && openCommentPanel,
			[shouldShowCommentOption, commentFlyoutOpen, isRedactionOpen, isRedactionInProgress, openCommentPanel],
		);

		const kebabMenuOptionalProps = {
			...(shouldShowCommentOption && { openCommentPanel, hasAccessToRecapComments, isRedactionInProgress }),
		};

		const menu = (
			<SegmentKebabMenu
				sameSpeakerAsPreviousSegment={sameSpeakerAsPreviousSegment}
				onCopyClick={onCopyClick}
				speakerNameToUserMapping={speakerNameToUserMapping}
				segment={segment}
				recordingId={meetingRecording.id}
				planHasRedaction={planHasRedaction}
				enterRedactionMode={!inRedactionMode && redactionIsVisible ? enterRedactionMode : undefined}
				addRedactingSpan={redactionIsVisible ? addRedactingSpan : undefined}
				preventRelabeling={preventRelabeling}
				{...kebabMenuOptionalProps}
			/>
		);

		if (isKeyEvent) {
			if (inRedactionMode) return null;
			return (
				<li ref={containerRef} className={styles.keyEventSegmentContainer}>
					<span className={styles.keyEventSegmentAvatar}>{segment.keyEventIcon ?? null}</span>
					<p ref={transcriptTextRef} className={cx(styles.transcriptSegmentText, styles.keyEventSegmentText)}>
						{!searchMatches && "reactNode" in segment ? segment.reactNode : markedText}{" "}
						{/* zero-width non-breaking space; using margin to make space between
						timestamp and text */}
						&#8288;
						<VideoTimestamp
							className={cx(styles.transcriptSegmentTime, styles.keyEventSegmentTime)}
							timestamp={segment.words[0].start_timestamp}
							videoDuration={videoDuration}
							onClick={onTimestampClick}
						/>
					</p>
				</li>
			);
		}

		const selectionStartIsWithinSegment = currentSpanStart != null && isStartWithinSegment(segment, currentSpanStart);
		const selectionEndIsWithinSegment = currentSpanEnd != null && isEndWithinSegment(segment, currentSpanEnd);

		const wholeSegmentRedacted =
			activeRedactions?.length == 1 &&
			activeRedactions[0][0] <= segment.words[0].start_timestamp &&
			activeRedactions[0][1] >= segment.words[segment.words.length - 1].end_timestamp;

		const isCommentSelected =
			selectedCommentTimestampStart &&
			selectedCommentTimestampEnd &&
			isSameFloatNumber(selectedCommentTimestampStart, segment.words[0].start_timestamp, TIME_COMPARISON_TOLERANCE) &&
			isSameFloatNumber(selectedCommentTimestampEnd, segment.words[0].end_timestamp, TIME_COMPARISON_TOLERANCE);

		return (
			<li
				ref={mergeRefs(containerRef, commentHoverFloating.refs.setReference)}
				className={cx(styles.transcriptSegmentContainer, {
					[styles.inRedactionMode]: inRedactionMode,
					[styles.commentOpen]: isCommentSelected,
				})}
				onClick={inRedactionMode ? undefined : onSegmentClick}
			>
				{inRedactionMode ? (
					<span className={styles.redactionMarkerGutter}>
						{selectionStartIsWithinSegment ? (
							<RedactionMarker className={styles.redactionMarker} selected="start" />
						) : inRedactionMode && (currentSpanEnd === null || currentSpanEnd > segment.words[0].start_timestamp) ? (
							<RedactionMarker
								className={styles.redactionMarker}
								selected="selecting-start"
								onClick={onClickRedactionStartMarkerImpl}
							/>
						) : (
							<RedactionMarker className={styles.redactionMarker} selected="not-allowed" />
						)}
						{selectionEndIsWithinSegment ? (
							<RedactionMarker className={styles.redactionMarker} selected="end" />
						) : currentSpanStart != null && currentSpanStart < segment.words[segment.words.length - 1].end_timestamp ? (
							<RedactionMarker
								className={styles.redactionMarker}
								selected="selecting-end"
								onClick={onClickRedactionEndMarkerImpl}
							/>
						) : (
							<RedactionMarker className={styles.redactionMarker} selected="not-allowed" />
						)}
					</span>
				) : (
					<Avatar
						key={avatarUser.fullName}
						user={avatarUser}
						size="small"
						className={cx(styles.transcriptSegmentAvatar, { [styles.hidden]: sameSpeakerAsPreviousSegment })}
					/>
				)}
				<div style={{ flex: "1 1 0" }}>
					{sameSpeakerAsPreviousSegment ? (
						menu
					) : (
						<div className={styles.transcriptSegmentSpeakerAndTime}>
							{preventRelabeling ? (
								<span className={cx({ [styles.redactedText]: wholeSegmentRedacted })}>{speaker}</span>
							) : (
								<SpeakerRelabelingSelector
									speakerToUserMapping={speakerNameToUserMapping}
									transcriptSegment={segment}
									recordingId={meetingRecording.id}
								>
									<span className={cx({ [styles.redactedText]: wholeSegmentRedacted })}>{speaker}</span>
								</SpeakerRelabelingSelector>
							)}
							<VideoTimestamp
								className={styles.transcriptSegmentTime}
								timestamp={segment.words[0].start_timestamp}
								videoDuration={videoDuration}
								onClick={inRedactionMode ? onTimestampClick : noop}
							/>
							{menu}
						</div>
					)}
					{!searchMatches && "reactNode" in segment ? segment.reactNode : transcriptText}

					{commentData && openCommentPanel && !isRedactionInProgress && (
						<CommentPill data={commentData} onClick={openCommentsPanel} />
					)}
				</div>

				{shouldShowCommentFlyout && (
					<div
						className={styles.segmentHoverPanel}
						ref={commentHoverFloating.refs.setFloating}
						style={commentHoverFloating.floatingStyles}
						{...commentHoverInteractions.getFloatingProps()}
					>
						<Button
							size="slim"
							variant="tertiary"
							className={styles.segmentHoverPanelCommentButton}
							onClick={openCommentsPanel}
						>
							{hasAccessToRecapComments ? <FontAwesomeIcon icon={faCommentAltLines} /> : <UpgradeIcon />}

							<span>Comment…</span>
						</Button>
					</div>
				)}
			</li>
		);
	}),
);
TranscriptSegment.displayName = "TranscriptSegment";

const dismissLowLanguageConfidenceRegenerationBannerMutation = graphql`
	mutation TranscriptPanel_DismissLowLanguageConfidenceRegenerationBannerMutation($meetingRecordingId: ID!) {
		dismissLanguageRegenerationBanner(meetingRecordingId: $meetingRecordingId) {
			meetingRecording {
				showLowLanguageConfidenceRegenerationBanner
			}
		}
	}
`;

export type LowLanguageConfidenceScoreAnalyticsDataType = {
	noteId: string;
	recordingId: string | null;
	detectedLanguage: string | null;
	languageConfidenceScore: number | null;
};

export function LowLanguageConfidenceRegenerationBanner({
	meetingRecording: meetingRecordingKey,
	className,
	noteId,
	trackLowLanguageConfidenceScoreBannerShown,
}: {
	meetingRecording: TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording$key;
	className?: string;
	noteId: string;
	trackLowLanguageConfidenceScoreBannerShown?: (analyticsData: LowLanguageConfidenceScoreAnalyticsDataType) => void;
}) {
	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording on MeetingRecordingInterface
			@argumentDefinitions(public: { type: "Boolean!" }) {
				... on MeetingRecording @skip(if: $public) {
					id
					detectedLanguage
					languageConfidenceScore
					...RegenerateForLanguageModalFragment_meetingRecording
				}
			}
		`,
		meetingRecordingKey,
	);

	const bannerTitleText = React.useMemo(() => {
		if (meetingRecording.detectedLanguage) {
			let languageText;
			if (languageCodeIsSupported(meetingRecording.detectedLanguage)) {
				const langInfo = getLanguageInformationForCode(meetingRecording.detectedLanguage);
				// don't show the native name in brackets if it's English
				const nativeName = !languageIsEnglish(langInfo.code) ? ` (${langInfo.nativeName})` : "";
				languageText = `${langInfo.englishName}${nativeName}`;
			} else {
				// fallback value if the language code is not found in the language data
				languageText = new Intl.DisplayNames(["en"], { type: "language" }).of(meetingRecording.detectedLanguage);
			}
			return (
				<>
					We detected that your meeting was in <strong>{languageText}</strong>. Did we get that right?
				</>
			);
		}
		return <>No language was detected for your meeting.</>;
	}, [meetingRecording]);

	const analyticsData: LowLanguageConfidenceScoreAnalyticsDataType = React.useMemo(() => {
		return {
			recordingId: meetingRecording.id || null,
			noteId: noteId,
			detectedLanguage: meetingRecording.detectedLanguage || null,
			languageConfidenceScore: meetingRecording.languageConfidenceScore || null,
		};
	}, [meetingRecording.detectedLanguage, meetingRecording.id, meetingRecording.languageConfidenceScore, noteId]);

	const modals = useModals();

	const openRegenerationModal = React.useCallback(async () => {
		const regenerateModalResult = await modals.open<boolean>(({ close }) => (
			<RegenerateForLanguageModal close={close} meetingRecording={meetingRecording} noteId={noteId} />
		));
		if (!regenerateModalResult) return;
		// else, do analytics
	}, [meetingRecording, modals, noteId]);

	const dismissBanner = React.useCallback(() => {
		if (meetingRecording.id === undefined) return;

		commitMutationPromise<DismissRegenerationBannerMutation>(environment, {
			mutation: dismissLowLanguageConfidenceRegenerationBannerMutation,
			variables: { meetingRecordingId: meetingRecording.id },
			onCompleted: () => {
				dispatchAnalyticsTrack("[AI Copilot] Low language confidence score banner dismissed", analyticsData);
			},
		});
	}, [analyticsData, meetingRecording.id]);

	React.useEffect(() => {
		if (trackLowLanguageConfidenceScoreBannerShown && analyticsData) {
			trackLowLanguageConfidenceScoreBannerShown(analyticsData);
		}
	});

	return (
		<div className={cx(styles.unlabeledSpeakersWarningBanner, className)}>
			<div className={styles.unlabeledSpeakersWarningIconContainer}>
				<FontAwesomeIcon icon={faLanguage} />
			</div>
			<p className={styles.unlabeledSpeakersWarningText}>
				{bannerTitleText}
				<br />
				<div className={styles.lowLanguageConfidenceRegenerationBannerButtons}>
					<ButtonLink onClick={openRegenerationModal} className={styles.labelSpeakersButtonLink}>
						Change Language
					</ButtonLink>
					<ButtonLink onClick={dismissBanner} className={styles.labelSpeakersButtonLink}>
						Dismiss
					</ButtonLink>
				</div>
			</p>
		</div>
	);
}

export function UnlabeledSpeakersWarning({
	meetingRecording: meetingRecordingKey,
	onLabelSpeakersClick,
	className,
}: {
	meetingRecording: TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording$key;
	onLabelSpeakersClick: () => void;
	className?: string;
}) {
	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording on MeetingRecordingInterface {
				transcript
				ownerAccount {
					__typename
				}
			}
		`,
		meetingRecordingKey,
	);

	const unknownSpeakerCount = React.useMemo(() => {
		const unknownSpeakers = new Set<string>();
		for (const segment of meetingRecording.transcript ?? []) {
			if (!segment.speaker_known) unknownSpeakers.add(segment.speaker);
		}
		return unknownSpeakers.size;
	}, [meetingRecording.transcript]);

	const isPublic = useIsPublic();
	const isGuest = window.INITIAL_STATE.user?.type === "GUEST";
	const isXWC = meetingRecording.ownerAccount.__typename === "ExternalAccount";

	if (isPublic || isGuest || isXWC || unknownSpeakerCount === 0) return null;

	return (
		<div className={cx(styles.unlabeledSpeakersWarningBanner, className)}>
			<div className={styles.unlabeledSpeakersWarningIconContainer}>
				<FontAwesomeIcon icon={faExclamationCircle} />
			</div>
			<p className={styles.unlabeledSpeakersWarningText}>
				We&apos;ve detected that{" "}
				<strong>
					{unknownSpeakerCount} {nGetText(unknownSpeakerCount, "speaker", "speakers")}
				</strong>{" "}
				in this meeting <strong>have not been assigned</strong> to a meeting attendee.
				<br />
				<ButtonLink onClick={onLabelSpeakersClick} className={styles.labelSpeakersButtonLink}>
					Label speakers now
				</ButtonLink>
			</p>
		</div>
	);
}

export function openRelabelMultipleSpeakersModal(
	meetingRecording: TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording$key,
	analyticsFingerprint: string,
) {
	dispatchAnalyticsTrack("[AI Copilot] Opened relabel multiple speakers modal", {
		fingerprint: analyticsFingerprint,
		meetingRecording,
	});

	__globalModalContext?.open(({ close }) => (
		<RelabelMultipleSpeakersModal meetingRecording={meetingRecording} close={close} />
	));
}

interface SpeakerRelabelFormRef {
	updateProgress(progress: number | undefined): void;
}

const CUSTOM_COMPONENTS: React.ComponentProps<typeof CreatableSelector>["components"] = {
	ClearIndicator(props) {
		const { innerProps, getStyles } = props;
		const { opacity: _, ...styles } = getStyles("clearIndicator", props);
		return (
			<div style={styles as React.CSSProperties} {...innerProps}>
				<FontAwesomeIcon icon={faTimes} />
			</div>
		);
	},
};

const workspaceUsersQuery = graphql`
	query TranscriptPanelWorkspaceUsersQuery($search: String) {
		users(first: 10, search: $search) {
			edges {
				node {
					id
					email
					fullName
					image
				}
			}
		}
	}
`;

const SpeakerRelabelForm = React.forwardRef<
	SpeakerRelabelFormRef,
	{
		transcriptSegment: SpeakerTranscriptSegment;
		onSpeakerLabelChange: (speaker: string, label: string | undefined, userId: string | undefined) => void;
		playAudio: (start: number, end: number) => void;
		stopAudio: () => void;
		playing: boolean;
		attendees: TranscriptPanel_SpeakerRelabelForm_attendees$key;
		label: string | undefined;
		disabled: boolean;
	}
>(function SpeakerRelabelForm(
	{ attendees: attendeesKey, label, transcriptSegment, onSpeakerLabelChange, playAudio, stopAudio, playing, disabled },
	ref,
) {
	const attendees = useFragment(
		graphql`
			fragment TranscriptPanel_SpeakerRelabelForm_attendees on RecordingParticipant @relay(plural: true) {
				email
				fullName
				image
				isTopUser
				user {
					id
				}
			}
		`,
		attendeesKey,
	);

	const speaker = getSegmentSpeaker(transcriptSegment);
	const startTimestamp = transcriptSegment.words[0].start_timestamp,
		endTimestamp = transcriptSegment.words[transcriptSegment.words.length - 1].end_timestamp;
	const buttonRef = React.useRef<HTMLButtonElement>(null);

	const play = React.useCallback(() => {
		playAudio(startTimestamp, endTimestamp);
	}, [playAudio, startTimestamp, endTimestamp]);

	const stop = React.useCallback(() => {
		stopAudio();
	}, [stopAudio]);

	React.useImperativeHandle(
		ref,
		() => ({
			updateProgress(progress) {
				if (!buttonRef.current) return;

				const percentage = ((progress ?? 0) * 100).toFixed(2);
				const gradientOpts = [
					"to right",
					`rgba(var(--veryVividRoyal-rgb), 0.2) 0 ${percentage}%`,
					`var(--secondaryButtonHoverColor) ${percentage}% 100%`,
				].join(", ");
				buttonRef.current.style.background = `linear-gradient(${gradientOpts})`;
			},
		}),
		[],
	);

	interface Option {
		label: string;
		value: string;
		attendee?: {
			email: string | null;
			fullName: string;
			image: string | null;
			isTopUser?: boolean;
			user?: { id: string } | null;
		};
	}

	const options = React.useMemo<readonly Option[]>(
		() =>
			attendees.map(attendee => ({
				label: attendee.fullName,
				value: attendee.email ?? attendee.fullName,
				attendee,
			})),
		[attendees],
	);

	const onLabelChange = React.useCallback(
		(newValue: Option | null) => {
			onSpeakerLabelChange(transcriptSegment.speaker, newValue?.label, newValue?.attendee?.user?.id);
		},
		[transcriptSegment.speaker, onSpeakerLabelChange],
	);

	const formatOptionLabel = React.useCallback(({ attendee, label }: Option) => {
		let avatarUser: React.ComponentProps<typeof Avatar>["user"];
		if (attendee) {
			avatarUser = {
				fullName: attendee.fullName,
				image: attendee.image ?? undefined,
			};
		} else {
			avatarUser = {
				fullName: label,
			};
		}

		return (
			<>
				<Avatar size="small" user={avatarUser} className={styles.speakerLabelOptionAvatar} />
				{label}
			</>
		);
	}, []);

	// NOTE: this function needs to return a string because we use the label as
	// the fullName and email in the user passed to avatar in formatOptionLabel
	const formatCreateLabel = React.useCallback((speakerLabel: string): string => {
		return `Add "${speakerLabel}" as speaker`;
	}, []);

	const selectedOption = React.useMemo(() => {
		if (!label) return null;

		const option = options.find(option => option.label === label);
		if (option) return option;
		else return { label, value: label };
	}, [label, options]);

	const loadOptions = React.useCallback(
		(inputValue: string, callback: (options: readonly Option[]) => void) => {
			if (!inputValue || inputValue.length < 2) {
				// Return meeting attendees as default options
				callback(options);
				return;
			}

			// Filter default options based on search query
			const filteredDefaultOptions = options.filter(option =>
				option.label.toLowerCase().includes(inputValue.toLowerCase()),
			);

			fetchQuery<TranscriptPanelWorkspaceUsersQuery>(
				environment,
				workspaceUsersQuery,
				{ search: inputValue },
				{ fetchPolicy: "network-only" },
			)
				.toPromise()
				.then(data => {
					if (!data?.users?.edges) {
						callback(filteredDefaultOptions); // Return filtered default options if no search results
						return;
					}

					// Transform search results into options
					const workspaceUsers = data.users.edges.map(edge => {
						const user = edge.node;
						return {
							label: user.fullName,
							value: user.email ?? user.fullName,
							attendee: {
								email: user.email,
								fullName: user.fullName,
								image: user.image,
								user: { id: user.id },
							},
						};
					});

					// Combine filtered default options with workspace users
					// but avoid duplicates by checking if a workspace user is already in the filtered defaults
					const combinedOptions = [...filteredDefaultOptions];

					workspaceUsers.forEach(user => {
						if (!combinedOptions.some(option => option.value === user.value)) {
							combinedOptions.push(user);
						}
					});

					callback(combinedOptions);
				})
				.catch(() => {
					// If there's an error, fall back to the filtered default options
					callback(filteredDefaultOptions);
				});
		},
		[options],
	);

	// Debounce search to avoid excessive API calls
	const debouncedLoadOptions = React.useMemo(() => debounce(loadOptions, 300), [loadOptions]);

	return (
		<div className={styles.speakerRelabelForm}>
			<Tooltip content={playing ? "Stop playing" : "Listen to snippet"} className={styles.listenButtonTooltip}>
				<Button
					ref={buttonRef}
					variant="secondary"
					onClick={playing ? stop : play}
					className={cx(styles.listenButton, { [styles.playing]: playing })}
					disabled={disabled}
				>
					<FontAwesomeIcon icon={faWaveform} className={styles.defaultIcon} />
					<FontAwesomeIcon icon={playing ? faStop : faPlay} className={styles.hoverIcon} />
				</Button>
			</Tooltip>
			<p className={styles.speakerRelabelFormSpeakerLabel} title={speaker}>
				{speaker}
			</p>
			<AsyncCreatableSelector<Option>
				className={styles.relabelMultipleSpeakersLabelSelector}
				isClearable
				isSearchable
				placeholder="Select a user"
				defaultOptions={options}
				loadOptions={debouncedLoadOptions}
				value={selectedOption}
				formatOptionLabel={formatOptionLabel}
				createOptionPosition="last"
				formatCreateLabel={formatCreateLabel}
				onChange={onLabelChange}
				components={CUSTOM_COMPONENTS}
				styles={{
					singleValue(parentStyles, props) {
						return {
							...parentStyles,
							...outlinedStyles.singleValue?.(
								parentStyles,
								// outlinedStyles uses `unknown` for the option type. That type
								// parameter is used as a parameter type in a function
								// somewhere inside `props`, and parameters are contravariant,
								// so we can't assign our version of the function to it (since
								// that parameter is typed as `Option`).
								// @ts-ignore
								props,
							),
							// Center the avatar and text of the selected option.
							alignItems: "center",
							marginLeft: 0,
						};
					},
				}}
			/>
		</div>
	);
});

function useRelabelMultipleSpeakersMutation(meetingRecordingId: string) {
	const [commit, isInFlight] = useMutationPromise<TranscriptPanelRelabelMultipleSpeakersMutation>(graphql`
		mutation TranscriptPanelRelabelMultipleSpeakersMutation(
			$meetingRecordingId: ID!
			$updates: [SpeakerLabelUpdatePayload!]!
		) {
			relabelMultipleRecordingSpeakers(meetingRecordingId: $meetingRecordingId, updates: $updates) {
				meetingRecording {
					transcript
				}
			}
		}
	`);

	const relabelMultipleSpeakers = React.useCallback(
		async (updates: TranscriptPanelRelabelMultipleSpeakersMutation["variables"]["updates"]) =>
			await commit({ variables: { meetingRecordingId, updates } }),
		[meetingRecordingId, commit],
	);

	return [relabelMultipleSpeakers, isInFlight] as const;
}

function RelabelMultipleSpeakersModal({
	meetingRecording: meetingRecordingKey,
	close,
}: {
	meetingRecording: TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording$key;
	close: () => void;
}) {
	const isPublic = useIsPublic();

	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording on MeetingRecordingInterface
			@argumentDefinitions(public: { type: "Boolean!" }) {
				__typename

				id
				transcript
				meetingRecordedVideoUrl

				... on MeetingRecording {
					allRelevantParticipants @skip(if: $public) {
						...TranscriptPanel_SpeakerRelabelForm_attendees
					}
					attendees @include(if: $public) {
						fullName
						email
						image
						isTopUser
					}
				}
				... on MeetingRecordingClip {
					baseRecording {
						allRelevantParticipants @skip(if: $public) {
							...TranscriptPanel_SpeakerRelabelForm_attendees
						}
						attendees @include(if: $public) {
							fullName
							email
							image
							isTopUser
						}
					}
				}
			}
		`,
		meetingRecordingKey,
	);

	const unknownSpeakerClips: Record<
		string,
		{
			speaker: string;
			duration: number;
			transcriptSegment: SpeakerTranscriptSegment;
			userId: string | undefined;
			autoLabeled: boolean;
		}
	> = {};

	for (const segment of meetingRecording.transcript ?? []) {
		const start = segment.words[0].start_timestamp,
			end = segment.words[segment.words.length - 1].end_timestamp,
			segmentDuration = end - start;
		if (
			segment.speaker_known ||
			(segment.speaker in unknownSpeakerClips && unknownSpeakerClips[segment.speaker].duration > segmentDuration)
		)
			continue;

		unknownSpeakerClips[segment.speaker] = {
			speaker: segment.speaker,
			duration: segmentDuration,
			transcriptSegment: segment,
			userId: segment.speaker_user_id == null ? undefined : urlsafeB64Encode(`User:${segment.speaker_user_id}`),
			autoLabeled: segment.auto_labeled ?? false,
		};
	}

	const orderedUnknownSpeakerClips = Object.values(unknownSpeakerClips).sort((clip1, clip2) => {
		if (clip1.speaker < clip2.speaker) return -1;
		else if (clip1.speaker > clip2.speaker) return 1;
		else return 0;
	});

	const audioRef = React.useRef<HTMLAudioElement>(null);
	const formRefs = React.useRef<Map<number, SpeakerRelabelFormRef>>(new Map());
	const makeFormRefHandler = React.useMemo(
		() =>
			memoize((start: number) => (ref: SpeakerRelabelFormRef | null) => {
				if (ref) formRefs.current.set(start, ref);
				else formRefs.current.delete(start);
			}),
		[],
	);

	const [labelBySpeaker, setLabelBySpeaker] = React.useState<
		Readonly<{ [k: string]: { label: string; userId?: string } }>
	>(() => {
		const labels: { [k: string]: { label: string; userId?: string } } = {};
		for (const clip of orderedUnknownSpeakerClips) {
			if (clip.autoLabeled) {
				labels[clip.speaker] = { label: clip.speaker, userId: clip.userId };
			}
		}
		return labels;
	});
	const onSpeakerChange = React.useCallback(
		(speaker: string, label: string | undefined, userId: string | undefined) => {
			if (label === undefined) {
				setLabelBySpeaker(({ [speaker]: _, ...rest }) => rest);
			} else {
				setLabelBySpeaker(prev => ({ ...prev, [speaker]: { label, userId } }));
			}
		},
		[],
	);

	const [playingRange, setPlayingRange] = React.useState<[start: number, end: number] | undefined>(undefined);

	const stopAudio = React.useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause();
			// Clear the src so the user can't resume it with the OS media controls.
			audioRef.current.src = "";
			if (playingRangeRef.current) {
				formRefs.current.get(playingRangeRef.current[0])?.updateProgress(undefined);
			}
			setPlayingRange(undefined);
		}
	}, []);

	const { analyticsFingerprint } = React.useContext(BillingContext);

	const playAudio = React.useCallback(
		(start: number, end: number) => {
			const audio = audioRef.current;
			if (!audio) return;

			stopAudio();

			audio.src = meetingRecording.meetingRecordedVideoUrl ?? "";
			audio.currentTime = start;
			audio.play();
			setPlayingRange([start, end]);

			dispatchAnalyticsTrack("[AI Copilot] Played audio in multiple speakers modal", {
				fingerprint: analyticsFingerprint,
				meetingRecording,
			});
		},
		[analyticsFingerprint, meetingRecording, stopAudio],
	);

	const playingRangeRef = React.useRef(playingRange);
	playingRangeRef.current = playingRange;
	const onTimeUpdate = React.useCallback(() => {
		const audio = audioRef.current,
			playingRange = playingRangeRef.current;
		if (!audio || playingRange === undefined) return;
		const [start, end] = playingRange;
		const formRef = formRefs.current.get(start);
		if (audio.currentTime >= end) {
			stopAudio();
			formRef?.updateProgress(undefined);
		} else {
			formRef?.updateProgress(Math.max(0, Math.min(1, (audio.currentTime - start) / (end - start))));
		}
	}, [stopAudio]);

	const onCancelClick = React.useCallback(() => close(), [close]);

	const [relabelMultipleSpeakers, isInFlight] = useRelabelMultipleSpeakersMutation(meetingRecording.id);

	const onSaveClick = React.useCallback(async () => {
		const updates = Object.entries(labelBySpeaker).map(([speaker, { label, userId }]) => ({
			speaker,
			label,
			userId,
		}));

		if (updates.length === 0) {
			close();
			return;
		}

		await relabelMultipleSpeakers(updates);
		close();

		const s = plural(updates.length);
		toast.success(`Successfully relabeled ${updates.length} speaker label${s}`);
	}, [labelBySpeaker, relabelMultipleSpeakers, close]);

	if (
		isPublic ||
		!meetingRecording ||
		(meetingRecording.__typename !== "MeetingRecording" && meetingRecording.__typename !== "MeetingRecordingClip")
	)
		return null;

	const attendees =
		meetingRecording.__typename === "MeetingRecording"
			? (meetingRecording.allRelevantParticipants ?? [])
			: (meetingRecording.baseRecording?.allRelevantParticipants ?? []);

	return (
		<Modal onClose={close} className={styles.relabelMultipleSpeakersModal}>
			<Modal.Header noBottomBorder>
				<Modal.Title>
					<BetaLabel className={styles.betaLabel} />
					Label multiple speakers
				</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon articleId="10485986" title="Copilot for hybrid and in-person meetings" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.relabelMultipleSpeakersModalBody}>
				<audio ref={audioRef} onTimeUpdate={onTimeUpdate} className={styles.relabelMultipleSpeakersModalAudio} />
				<p className={styles.relabelMultipleSpeakersModalInstructions}>Assign a user to each speaker below:</p>
				<div className={styles.relabelMultipleSpeakersFormList}>
					{orderedUnknownSpeakerClips.map(({ speaker, transcriptSegment }) => {
						const start = transcriptSegment.words[0].start_timestamp;
						const playing = playingRange?.[0] === start;

						return (
							<SpeakerRelabelForm
								ref={makeFormRefHandler(start)}
								key={speaker}
								label={labelBySpeaker[speaker]?.label}
								transcriptSegment={transcriptSegment}
								onSpeakerLabelChange={onSpeakerChange}
								playAudio={playAudio}
								stopAudio={stopAudio}
								playing={playing}
								attendees={attendees}
								disabled={!Boolean(meetingRecording.meetingRecordedVideoUrl)}
							/>
						);
					})}
				</div>
				<div className={styles.relabelMultipleSpeakersModalBanner}>
					<FontAwesomeIcon icon={faExclamationCircle} className={styles.bannerIcon} />
					<p>
						Changes will apply to <strong>all segments</strong> spoken by the same speaker.
					</p>
				</div>
			</Modal.Body>
			<Modal.Footer noTopBorder className={styles.relabelMultipleSpeakersModalFooter}>
				<Button variant="secondary" onClick={onCancelClick}>
					Cancel
				</Button>
				<Button variant="primary" onClick={onSaveClick} disabled={isInFlight}>
					Save
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

const KEY_EVENT_ICONS: Record<string, React.ReactNode | undefined> = {
	join: <FontAwesomeIcon className={styles.keyEventIcon} icon={faUserCircle} />,
	leave: <FontAwesomeIcon className={styles.keyEventIcon} icon={faMinus} />,
	/* eslint-disable camelcase */
	screenshare_start: <FontAwesomeIcon className={styles.keyEventIconSmall} icon={faDesktop} />,
	screenshare_stop: <ScreenShareStopIcon className={styles.keyEventIconScreenShareStop} />,
	pause_recording: <FontAwesomeIcon className={styles.keyEventIcon} icon={faPause} />,
	redaction: <FontAwesomeIcon className={styles.keyEventIcon} icon={faDeleteRightSolid} />,
	/* eslint-enable camelcase */
};

export interface TranscriptRef {
	handleTimeUpdate: (currentTime: number) => void;
	closeCommentsPanel: () => void;
}

const SegmentKebabMenu = React.memo(function SegmentKebabMenu({
	sameSpeakerAsPreviousSegment,
	onCopyClick,
	speakerNameToUserMapping,
	segment,
	recordingId,
	planHasRedaction,
	enterRedactionMode,
	addRedactingSpan,
	preventRelabeling,
	openCommentPanel,
	hasAccessToRecapComments,
	isRedactionInProgress,
}: {
	sameSpeakerAsPreviousSegment: boolean;
	onCopyClick: () => void;
	speakerNameToUserMapping: SpeakerToUserMapping;
	segment: SpeakerTranscriptSegment;
	recordingId: string;
	planHasRedaction: boolean;
	/** Should be undefined if we're already in redaction mode */
	enterRedactionMode?: (analyticsLocation: string) => void;
	/** Should be undefined if redaction is unavailable */
	addRedactingSpan?: (span: RedactingSpan) => void;
	preventRelabeling: boolean;
	openCommentPanel?: (start: number, end: number) => void;
	hasAccessToRecapComments?: boolean;
	isRedactionInProgress?: boolean;
}) {
	const segmentSpeakerUser = speakerNameToUserMapping[segment.speaker];

	const avatarUser: AvatarUserAttrs = React.useMemo(() => {
		const mappingSpeaker = segmentSpeakerUser;
		if (mappingSpeaker) {
			return { ...mappingSpeaker, email: mappingSpeaker.email ?? "" };
		}
		return { email: "", fullName: segment.speaker };
	}, [segment.speaker, segmentSpeakerUser]);

	const redact = React.useCallback(() => {
		if (!addRedactingSpan) throw new Error("addRedactingSpan not provided, option shouldn't have been shown");
		if (enterRedactionMode) {
			enterRedactionMode("transcript-segment-kebab-menu");
		}
		addRedactingSpan([segment.words[0].start_timestamp, segment.words[segment.words.length - 1].end_timestamp]);
	}, [addRedactingSpan, enterRedactionMode, segment]);

	const onComment = React.useCallback(() => {
		if (!openCommentPanel) return;

		openCommentPanel(segment.words[0].start_timestamp, segment.words[0].end_timestamp);
	}, [openCommentPanel, segment]);

	return (
		<Menu>
			<Menu.Trigger onClick={preventDefault}>
				<IconButton
					variant="tertiary"
					icon={faEllipsisV}
					className={cx(styles.transcriptSegmentMenuTrigger, { [styles.floatRight]: sameSpeakerAsPreviousSegment })}
				/>
			</Menu.Trigger>
			<Menu.Content onClick={preventDefault}>
				<Menu.Item onSelect={onCopyClick} icon={faLink}>
					Copy link
				</Menu.Item>

				{openCommentPanel && !isRedactionInProgress && (
					<>
						{(!preventRelabeling || !!addRedactingSpan) && <Menu.Separator />}

						<Menu.Item onSelect={onComment} icon={faCommentAltLines}>
							Comment…
							{!hasAccessToRecapComments && (
								<>
									{" "}
									&ensp; <UpgradeIcon />{" "}
								</>
							)}
						</Menu.Item>
					</>
				)}

				{!preventRelabeling && (
					<Menu.Sub>
						<Menu.SubTrigger user={avatarUser}>Change speaker…</Menu.SubTrigger>
						<Menu.SubContent data-ignore-focus-lock>
							<SpeakerRelabelingOptions
								speakerToUserMapping={speakerNameToUserMapping}
								transcriptSegment={segment}
								recordingId={recordingId}
							/>
						</Menu.SubContent>
					</Menu.Sub>
				)}
				{addRedactingSpan && (
					<Menu.Item onSelect={redact} icon={faDeleteRightLight}>
						{enterRedactionMode ? "Redact…" : "Add to redaction"}
						{!planHasRedaction && (
							<>
								&ensp;
								<UpgradeIcon />
							</>
						)}
					</Menu.Item>
				)}
			</Menu.Content>
		</Menu>
	);
});

function formatDurationTime(duration: number): string {
	let dur = Math.floor(duration);
	if (dur <= 60) {
		return `${dur} sec`;
	} else {
		return `${Math.floor(dur / 60)} min ${dur % 60} sec`;
	}
}

export function getEventDescriptionByEventType(
	eventType: string,
	pronoun: string,
	duration: number | null = null,
): string | undefined {
	return {
		join: "joined",
		leave: "left",
		/* eslint-disable camelcase */
		screenshare_start: `started sharing ${pronoun} screen`,
		screenshare_stop: `stopped sharing ${pronoun} screen`,
		pause_recording:
			duration && duration > 0 ? `Recording paused for ${formatDurationTime(duration)}` : "Recording paused",
		resume_recording: "Recording resumed",
		/* if we have a duration, this event represents the whole span, otherwise it's just the start */
		redaction: duration && duration > 0 ? `Content redacted (${formatDurationTime(duration)})` : "Redaction started",
		redaction_end: "Redaction ended",
		/* eslint-enable camelcase */
	}[eventType];
}

function combineAndConvertKeyEvents(
	keyEvents: KeyEvent[],
	currentUserName: string | undefined,
): SpeakerTranscriptSegment[] {
	if (!keyEvents || keyEvents.length === 0) {
		return [];
	}

	// Combine events
	let combinedEvents: KeyEvent[] = [];
	let currentEvent = keyEvents[0];
	let sameEventParticipants = [currentEvent.participantName];
	for (let i = 1; i < keyEvents.length; i++) {
		if (currentEvent.duration && currentEvent.duration > 0) {
			combinedEvents.push(currentEvent);
			currentEvent = keyEvents[i];
		} else if (
			keyEvents[i].eventType === currentEvent.eventType &&
			currentEvent.eventType !== "pause_recording" &&
			currentEvent.eventType !== "redaction"
		) {
			sameEventParticipants.push(keyEvents[i].participantName);
		} else {
			if (keyEvents[i].eventType !== "pause_recording") {
				combinedEvents.push({
					eventType: currentEvent.eventType,
					participantName: sameEventParticipants.join(", "),
					timeFromRecordingStart: currentEvent.timeFromRecordingStart,
					endTimeFromRecordingStart: currentEvent.endTimeFromRecordingStart,
					duration: 0,
				});
				sameEventParticipants = [currentEvent.participantName];
			}
			currentEvent = keyEvents[i];
		}
	}

	if (currentEvent.duration && currentEvent.duration > 0) {
		combinedEvents.push(currentEvent);
	} else {
		// Last event in list
		combinedEvents.push({
			eventType: currentEvent.eventType,
			participantName: sameEventParticipants.join(", "),
			timeFromRecordingStart: currentEvent.timeFromRecordingStart,
			endTimeFromRecordingStart: currentEvent.endTimeFromRecordingStart,
			duration: currentEvent.duration,
		});
	}

	// Convert to transcript segments
	return combinedEvents.map((event: KeyEvent) => {
		const eventParticipants = event.participantName ? event.participantName : "";
		const eventUsers = eventParticipants.split(", ");
		const firstNameAndInitials = eventUsers.map((user: string) => {
			if (user === currentUserName) {
				return "You";
			}
			// We only want first names unless there are multiple users with thesame first name,
			// in which case we want to display the initial of their last name as well.
			const firstName = user.split(" ")[0];
			const hasLastName = user.split(" ").length > 1;
			// There are probably more efficient ways to do this, but it's unlikely that
			// there will be a large number of users performing the same key event at once
			const duplicateFirstNameExists = eventUsers.filter(u => u.split(" ")[0] === firstName).length > 1;
			const lastName = hasLastName && duplicateFirstNameExists ? user.split(" ")[1][0] : "";
			return duplicateFirstNameExists ? `${firstName} ${lastName}` : firstName;
		});

		const description = getEventDescriptionByEventType(
			event.eventType,
			eventParticipants.trim() === currentUserName ? "your" : "their",
			event.duration,
		);

		return {
			words: [
				{
					text: `${event.participantName ?? ""} ${description}`,
					// eslint-disable-next-line camelcase
					start_timestamp: event.timeFromRecordingStart,
					// eslint-disable-next-line camelcase
					end_timestamp: event.timeFromRecordingStart,
				},
			],
			speaker: eventParticipants,
			segmentType: TranscriptSegmentType.keyEvent,
			keyEventIcon: KEY_EVENT_ICONS[event.eventType],
			reactNode: (
				<>
					<SimpleUserList names={firstNameAndInitials} limit={2} boldNames /> {description}
				</>
			),
		};
	});
}

graphql`
	fragment TranscriptPanel_recordingParticipants on MeetingRecordingInterface {
		... on MeetingRecording {
			allRelevantParticipants {
				fullName
				email
				image
				isTopUser
				user {
					id
				}
			}
		}
		... on MeetingRecordingClip {
			baseRecording {
				allRelevantParticipants {
					fullName
					email
					image
					isTopUser
					user {
						id
					}
				}
			}
		}
	}
`;

function useSpeakerNameToUserMapping(
	meetingRecordingKey: TranscriptPanel_makeSpeakerNameToUserMapping_meetingRecording$key,
) {
	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptPanel_makeSpeakerNameToUserMapping_meetingRecording on MeetingRecordingInterface
			@argumentDefinitions(public: { type: "Boolean!" }) {
				... on MeetingRecording {
					...TranscriptPanel_recordingParticipants @relay(mask: false) @skip(if: $public)
					attendees @include(if: $public) {
						fullName
						email
						image
						isTopUser
					}
				}
				... on MeetingRecordingClip {
					baseRecording {
						...TranscriptPanel_recordingParticipants @relay(mask: false) @skip(if: $public)
						attendees @include(if: $public) {
							fullName
							email
							image
							isTopUser
						}
					}
				}
			}
		`,
		meetingRecordingKey,
	);

	return React.useMemo(() => {
		const mapping: SpeakerToUserMapping = {};
		const participants =
			meetingRecording.allRelevantParticipants ?? meetingRecording.baseRecording?.allRelevantParticipants ?? [];
		for (const participant of participants) {
			mapping[participant.fullName] = {
				fullName: participant.fullName,
				email: participant.email ?? undefined,
				image: participant.image ?? undefined,
				isTopUser: participant.isTopUser,
				user: participant.user ?? undefined,
			};
		}
		return mapping;
	}, [meetingRecording]);
}

const relabelRecordingSpeakerAsUserMutation = graphql`
	mutation TranscriptPanelRelabelSpeakerAsUserMutation($meetingRecordingId: ID!, $userId: ID!, $currentLabel: String!) {
		relabelRecordingSpeakerAsUser(
			meetingRecordingId: $meetingRecordingId
			userId: $userId
			currentLabel: $currentLabel
		) {
			meetingRecording {
				id
				transcript
				...TranscriptPanel_recordingParticipants
				...TranscriptModalContentStats
			}
		}
	}
`;

const relabelRecordingSpeakerWithoutUserMutation = graphql`
	mutation TranscriptPanelRelabelSpeakerWithoutUserMutation(
		$meetingRecordingId: ID!
		$newLabel: String!
		$currentLabel: String!
	) {
		relabelRecordingSpeaker(meetingRecordingId: $meetingRecordingId, newLabel: $newLabel, currentLabel: $currentLabel) {
			meetingRecording {
				id
				transcript
				...TranscriptPanel_recordingParticipants
				...TranscriptModalContentStats
			}
		}
	}
`;

const relabelRecordingSpeakerInSegmentMutation = graphql`
	mutation TranscriptPanelRelabelSpeakerInSegmentMutation(
		$meetingRecordingId: ID!
		$segmentStartTime: Float!
		$newLabel: String!
	) {
		relabelRecordingSpeakerInSegment(
			meetingRecordingId: $meetingRecordingId
			segmentStartTime: $segmentStartTime
			newLabel: $newLabel
		) {
			meetingRecording {
				id
				transcript
				...TranscriptPanel_recordingParticipants
				...TranscriptModalContentStats
			}
		}
	}
`;

const relabelRecordingSpeakerInSegmentAsUserMutation = graphql`
	mutation TranscriptPanelRelabelSpeakerInSegmentAsUserMutation(
		$meetingRecordingId: ID!
		$segmentStartTime: Float!
		$userId: ID!
	) {
		relabelRecordingSpeakerInSegmentAsUser(
			meetingRecordingId: $meetingRecordingId
			segmentStartTime: $segmentStartTime
			userId: $userId
		) {
			meetingRecording {
				id
				transcript
				...TranscriptPanel_recordingParticipants
				...TranscriptModalContentStats
			}
		}
	}
`;

function RelabelConfirmationModal({ close }: ModalProps<string | null>) {
	const [relabelOption, setRelabelOption] = React.useState<"all" | "single">("single");

	const onChangeRelabelOption = React.useCallback((option: "all" | "single") => {
		setRelabelOption(option);
	}, []);

	const closeWithoutRelabeling = React.useCallback(() => {
		close(null);
	}, [close]);

	const closeWithRelabeling = React.useCallback(() => {
		close(relabelOption);
	}, [close, relabelOption]);

	return (
		<Modal onClose={closeWithoutRelabeling}>
			<Modal.Header onClose={closeWithoutRelabeling}>
				<Modal.Title>Relabel all?</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<RadioGroup
					value={relabelOption}
					options={[
						{ value: "single", label: <p>This snippet only</p> },
						{ value: "all", label: <p>All snippets by this speaker</p> },
					]}
					onChange={onChangeRelabelOption}
				/>
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={closeWithoutRelabeling} variant="secondary">
					Cancel
				</Button>
				<Button onClick={closeWithRelabeling} variant="primary">
					Ok
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

type TranscriptSpeaker = {
	fullName: string;
	email?: string;
	image?: string;
	isTopUser?: boolean;
	user?: { id: string };
};

function SpeakerOption({
	onSelectSpeaker,
	speaker,
}: {
	onSelectSpeaker: (speaker: TranscriptSpeaker) => void;
	speaker: TranscriptSpeaker;
}) {
	const onSelect = React.useCallback(() => {
		onSelectSpeaker(speaker);
	}, [onSelectSpeaker, speaker]);

	const speakerAvatarUser = React.useMemo(() => {
		return {
			id: speaker.user?.id ?? "",
			fullName: speaker.fullName,
			email: speaker.email ?? "",
			image: speaker.image,
			isTopUser: speaker.isTopUser,
			user: speaker.user,
		};
	}, [speaker.email, speaker.fullName, speaker.image, speaker.isTopUser, speaker.user]);

	return (
		<Menu.Item
			onSelect={onSelect}
			className={styles.speakerOption}
			user={speakerAvatarUser}
			// Set textValue to the empty string to disable typeahead (where radix
			// selects the first item matching what you're typing). This interferes
			// with the search input we put inside the menu.
			textValue=""
		>
			{speaker.fullName}
		</Menu.Item>
	);
}

function speakerSortFunc(a: TranscriptSpeaker, b: TranscriptSpeaker) {
	return a.fullName.localeCompare(b.fullName);
}

function SpeakerRelabelingOptions({
	speakerToUserMapping,
	transcriptSegment,
	recordingId,
}: {
	speakerToUserMapping: SpeakerToUserMapping;
	transcriptSegment: SpeakerTranscriptSegment;
	recordingId: string;
}) {
	const defaultSpeakerOptions: TranscriptSpeaker[] = React.useMemo(
		() => Object.values(speakerToUserMapping).sort(speakerSortFunc),
		[speakerToUserMapping],
	);

	const modals = useModals();

	const [search, setSearch] = React.useState("");

	const searchFieldRef = React.useRef<HTMLInputElement>(null);

	const debouncedSearch = useDebouncedValue(search, 300);

	const [speakerOptions, setSpeakerOptions] = React.useState<TranscriptSpeaker[]>(defaultSpeakerOptions);
	React.useEffect(() => {
		if (!debouncedSearch || debouncedSearch.length < 2) {
			setSpeakerOptions(defaultSpeakerOptions);
			return;
		}

		fetchQuery<TranscriptPanelWorkspaceUsersQuery>(
			environment,
			workspaceUsersQuery,
			{ search: debouncedSearch },
			{ fetchPolicy: "network-only" },
		)
			.toPromise()
			.then(data => {
				if (!data?.users?.edges) {
					return defaultSpeakerOptions;
				}

				// Convert workspace users to speaker format
				const workspaceUsers: TranscriptSpeaker[] = data.users.edges.map(edge => {
					const user = edge.node;
					return {
						fullName: user.fullName,
						email: user.email,
						image: user.image,
						user: { id: user.id },
					};
				});

				// Combine existing filtered speakers with workspace users, avoiding duplicates
				const currentFiltered = defaultSpeakerOptions.filter(speaker =>
					speaker.fullName.toLowerCase().includes(debouncedSearch.toLowerCase()),
				);

				const combinedSpeakers = [...currentFiltered];

				workspaceUsers.forEach(user => {
					if (!combinedSpeakers.some(speaker => speaker.email === user.email)) {
						combinedSpeakers.push(user);
					}
				});

				return combinedSpeakers;
			})
			.catch(error => {
				Sentry.captureException(error);
				return defaultSpeakerOptions;
			})
			.then(combinedSpeakers => {
				setSpeakerOptions(combinedSpeakers.sort(speakerSortFunc));
			});
	}, [debouncedSearch, defaultSpeakerOptions, speakerToUserMapping]);

	const speakerToCreate = React.useMemo(() => {
		const exactSpeakerMatch = speakerOptions.some(
			option => option.fullName.toLocaleLowerCase() === search.toLocaleLowerCase(),
		);
		if (!exactSpeakerMatch && search !== "") {
			return { fullName: search };
		}
		return undefined;
	}, [search, speakerOptions]);

	const filteredSpeakers = React.useMemo(() => {
		return speakerOptions
			.filter(option => option.fullName.toLocaleLowerCase().includes(search.toLowerCase()))
			.sort(speakerSortFunc);
	}, [speakerOptions, search]);

	const setSearchValue = React.useCallback((value: string) => {
		setSearch(value);
	}, []);

	const clearSearch = React.useCallback(() => {
		setSearchValue("");
	}, [setSearchValue]);

	const onSelectSpeaker = React.useCallback(
		async (speaker: TranscriptSpeaker) => {
			if (speaker.fullName == transcriptSegment.speaker) {
				return;
			}

			const relabelOption = await modals.open<string | null | undefined>(({ close }) => (
				<RelabelConfirmationModal close={close} />
			));

			if (!Boolean(relabelOption)) {
				return;
			}

			if (speaker.user) {
				if (relabelOption === "all") {
					commitMutationPromise(environment, {
						mutation: relabelRecordingSpeakerAsUserMutation,
						variables: {
							meetingRecordingId: recordingId,
							userId: speaker.user.id,
							currentLabel: transcriptSegment.speaker,
						},
					});
				} else {
					commitMutationPromise(environment, {
						mutation: relabelRecordingSpeakerInSegmentAsUserMutation,
						variables: {
							meetingRecordingId: recordingId,
							segmentStartTime: transcriptSegment.words[0].start_timestamp,
							userId: speaker.user.id,
						},
					});
				}
			} else if (relabelOption === "all") {
				commitMutationPromise(environment, {
					mutation: relabelRecordingSpeakerWithoutUserMutation,
					variables: {
						meetingRecordingId: recordingId,
						newLabel: speaker.fullName,
						currentLabel: transcriptSegment.speaker,
					},
				});
			} else {
				commitMutationPromise(environment, {
					mutation: relabelRecordingSpeakerInSegmentMutation,
					variables: {
						meetingRecordingId: recordingId,
						segmentStartTime: transcriptSegment.words[0].start_timestamp,
						newLabel: speaker.fullName,
						currentLabel: transcriptSegment.speaker,
					},
				});
			}
		},
		[modals, recordingId, transcriptSegment.speaker, transcriptSegment.words],
	);

	const createSpeaker = React.useCallback(() => {
		if (!speakerToCreate) {
			return;
		}

		onSelectSpeaker(speakerToCreate);
	}, [onSelectSpeaker, speakerToCreate]);

	React.useEffect(() => {
		searchFieldRef.current?.focus();
	}, [searchFieldRef]);

	return (
		<>
			<div className={styles.search}>
				<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
				<TextField
					ref={searchFieldRef}
					value={search}
					setValue={setSearchValue}
					placeholder="Type speaker's name"
					autoFocus
					className={styles.searchInput}
				/>
				<FontAwesomeIcon
					icon={faTimes}
					onClick={clearSearch}
					className={cx(styles.clear, { [styles.visible]: Boolean(search) })}
				/>
			</div>
			<Menu.Separator />
			<Menu.Label>Suggested speakers</Menu.Label>
			<div className={styles.speakerOptions}>
				{filteredSpeakers.map(speaker => (
					<SpeakerOption
						key={speaker.user?.id ?? speaker.email ?? speaker.fullName}
						onSelectSpeaker={onSelectSpeaker}
						speaker={speaker}
					/>
				))}
				{speakerToCreate && (
					<Menu.Item
						onSelect={createSpeaker}
						// Set textValue to the empty string to disable typeahead (where
						// radix selects the first item matching what you're typing). This
						// interferes with the search input we put inside the menu.
						textValue=""
					>
						Create &quot;{speakerToCreate.fullName}&quot;
					</Menu.Item>
				)}
			</div>
		</>
	);
}

function SpeakerRelabelingSelector({
	speakerToUserMapping,
	transcriptSegment,
	recordingId,
	children,
}: {
	speakerToUserMapping: SpeakerToUserMapping;
	transcriptSegment: SpeakerTranscriptSegment;
	recordingId: string;
	children: React.ReactNode;
}) {
	const [isOpen, setIsOpen] = React.useState(false);

	const toggleOpen = React.useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const onClick = React.useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

	return (
		<div onClick={onClick}>
			<Menu open={isOpen} onOpenChange={toggleOpen}>
				<Menu.Trigger>
					<Button className={cx(styles.speakerSelectButton, { [styles.menuOpen]: isOpen })}>
						{children}
						<FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
					</Button>
				</Menu.Trigger>
				<Menu.Content>
					<SpeakerRelabelingOptions
						speakerToUserMapping={speakerToUserMapping}
						transcriptSegment={transcriptSegment}
						recordingId={recordingId}
					/>
				</Menu.Content>
			</Menu>
		</div>
	);
}
export async function confirmDiscardRedactions() {
	return await confirm(<>The selected content will not be redacted.</>, {
		noText: "Keep redacting",
		yesText: "Exit redaction",
		title: "Exit redaction mode?",
		noHeaderBorder: true,
		className: styles.confirmDialog,
	});
}

function useFloatingElement<ParentType extends HTMLElement>() {
	const parentRef = React.useRef<ParentType | null>(null);

	const [isOpen, setIsOpen] = React.useState(false);
	const [selection, setSelection] = React.useState<Selection | null>(null);

	const { refs, floatingStyles } = useFloating({
		placement: "bottom",
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(6), inline(), flip(), shift()],
		whileElementsMounted: autoUpdate,
	});

	React.useEffect(() => {
		function onSelectionChange() {
			const selection = window.getSelection();
			if (!selection) {
				return;
			}

			let range: Range;
			try {
				range = selection.getRangeAt(0);
			} catch {
				setIsOpen(false);
				setSelection(null);
				return;
			}
			if (selection.isCollapsed || !range || !parentRef.current?.contains(range.commonAncestorContainer)) {
				setIsOpen(false);
				setSelection(null);
				return;
			}

			refs.setReference({
				getBoundingClientRect: () => range.getBoundingClientRect(),
				getClientRects: () => range.getClientRects(),
			});
			setIsOpen(true);
			setSelection(selection);
		}

		document.addEventListener("selectionchange", onSelectionChange);
		// const parentRefValue = parentRef.current;
		// parentRefValue?.addEventListener("mouseup", onSelectionChange);
		return () => {
			document.removeEventListener("selectionchange", onSelectionChange);
			// parentRefValue?.removeEventListener("mouseup", onSelectionChange);
		};
	}, [refs]);

	const FloatingElement = <div ref={refs.setFloating} style={{ ...floatingStyles, userSelect: "none" }} />;

	return [parentRef, selection, isOpen, FloatingElement] as const;
}

function ConfirmRedactionModal({
	onClose,
	canRegenerateSummary,
}: {
	onClose(value: [confirm: boolean, regenerateSummary: boolean]): void;
	canRegenerateSummary: boolean;
}) {
	const id = useID();
	const [inputValue, setInputValue] = React.useState("");
	const [regenerateSummary, _setRegenerateSummary] = React.useState(canRegenerateSummary);
	const setRegenerateSummary = React.useCallback(
		(checked: boolean | "indeterminate") => _setRegenerateSummary(checked === true && canRegenerateSummary),
		[canRegenerateSummary],
	);
	const confirm = React.useCallback(() => onClose([true, regenerateSummary]), [regenerateSummary, onClose]);
	const cancel = React.useCallback(() => onClose([false, regenerateSummary]), [regenerateSummary, onClose]);

	return (
		<Modal onClose={cancel} title="Confirm redaction" className={styles.confirmRedactionModal}>
			<Modal.Header onClose={cancel} noBottomBorder>
				<Modal.Title>Permanently redact selected content?</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form id={id}>
					<p style={{ font: "var(--heading-5)" }}>
						The selected content will be permanently redacted and deleted from both the transcript and recording. This
						action cannot be undone.
					</p>
					<label htmlFor={`${id}-text-confirmation`} style={{ font: "var(--heading-5-emphasis)" }}>
						Please type &lsquo;DELETE&rsquo; to confirm.
					</label>
					<SimpleInput
						id={`${id}-text-confirmation`}
						value={inputValue}
						onValueChange={setInputValue}
						name="redaction_confirmation"
					/>
				</form>
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<Modal.FooterSecondaryActions>
					<Tooltip
						disabled={canRegenerateSummary}
						content="This recording has reached the limit for recap regeneration."
					>
						<span style={{ alignItems: "center", display: "flex" }} tabIndex={canRegenerateSummary ? undefined : 0}>
							<Checkbox
								id={`${id}-regenerate-summary`}
								checked={regenerateSummary}
								onCheckedChange={setRegenerateSummary}
								disabled={!canRegenerateSummary}
							/>
							<label htmlFor={`${id}-regenerate-summary`}>&ensp;Regenerate AI Recap</label>
						</span>
					</Tooltip>
				</Modal.FooterSecondaryActions>
				<Button form={id} onClick={cancel} variant="secondary">
					Keep redacting
				</Button>
				<Button form={id} type="submit" onClick={confirm} variant="destructive" disabled={inputValue !== "DELETE"}>
					Redact content
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

const redactMutation = graphql`
	mutation TranscriptPanel_redactMutation(
		$meetingRecordingId: ID!
		$redactingSpans: [[Float!]!]
		$regenerateOnFinish: Boolean!
	) {
		redactSpansFromMeetingRecording(
			meetingRecordingId: $meetingRecordingId
			redactingSpans: $redactingSpans
			regenerateOnFinish: $regenerateOnFinish
		) {
			meetingRecording {
				...TranscriptModal_meetingRecording @arguments(public: false)
			}
		}
	}
`;

interface TranscriptProps {
	note: TranscriptPanel_note$key | null;
	meetingRecording: TranscriptPanel_Transcript_meetingRecording$key;
	viewer: TranscriptPanel_Transcript_viewer$key | null;
	transcriptDeletionReason: DeletionReason | null;
	transcript: readonly SpeakerTranscriptSegment[];
	onSegmentClick: (segment: SpeakerTranscriptSegment) => void;
	pauseVideo?: () => void;
	videoDuration: number | null;
	closeModal: () => void;
	mode?: TranscriptMode;
	setMode?: React.Dispatch<React.SetStateAction<TranscriptMode>>;
	makeSpaceForAskCopilotButton?: boolean;
	fromNote?: boolean;
	narrowViewport?: boolean;
	isClip?: boolean;
	trackLowLanguageConfidenceScoreBannerShown?: (analyticsData: LowLanguageConfidenceScoreAnalyticsDataType) => void;
}

function formatTranscriptAsText(transcript: readonly SpeakerTranscriptSegment[], videoDuration: number | null): string {
	if (!transcript || transcript.length === 0) {
		return "No transcript available.";
	}

	return transcript
		.map(segment => {
			const timestamp = formatTimestamp(segment.words[0].start_timestamp, videoDuration);
			const speaker = getSegmentSpeaker(segment);
			const text = segment.words.map(word => word.text).join(" ");
			return `[${timestamp}] ${speaker}: ${text}`;
		})
		.join("\n");
}

export const Transcript = React.forwardRef<TranscriptRef, TranscriptProps>(function Transcript(
	{
		note: noteKey,
		meetingRecording: meetingRecordingKey,
		viewer: viewerKey,
		transcriptDeletionReason,
		transcript,
		onSegmentClick: parentOnSegmentClick,
		pauseVideo,
		videoDuration,
		closeModal,
		mode,
		setMode,
		makeSpaceForAskCopilotButton,
		fromNote,
		narrowViewport,
		isClip,
		trackLowLanguageConfidenceScoreBannerShown,
	},
	ref,
) {
	const note = useFragment(
		graphql`
			fragment TranscriptPanel_note on Note {
				id
				isLocked
				viewerCanEdit
				noteuserSet {
					id
					user {
						id
						email
						fullName
						isGuest
						image
					}
				}
				event {
					organizer {
						id
					}
				}
				...TranscriptPanel_TranscriptSegment_note
			}
		`,
		noteKey,
	);

	const meetingRecording = useFragment<TranscriptPanel_Transcript_meetingRecording$key>(
		graphql`
			fragment TranscriptPanel_Transcript_meetingRecording on MeetingRecordingInterface
			@argumentDefinitions(public: { type: "Boolean!" }) {
				__typename
				id
				meetingRecordedVideoUrl
				... on MeetingRecording {
					activeSummary {
						id
					}
					canRegenerateSummary
					comments {
						threadId
						startTimestamp
						endTimestamp
						count
						users {
							id
							email
							fullName
							image
							isExternal
						}
					}
					showLowLanguageConfidenceRegenerationBanner
				}
				ownerAccount {
					__typename
				}
				...TranscriptPanel_TranscriptSegment_meetingRecording
				...Timeline_meetingKeyEvents
				...TranscriptPanel_makeSpeakerNameToUserMapping_meetingRecording @arguments(public: $public)
				...TranscriptPanel_RelabelMultipleSpeakersModal_meetingRecording @arguments(public: $public)
				...TranscriptPanel_UnlabeledSpeakersWarning_meetingRecording
				...TranscriptPanel_LowLanguageConfidenceRegenerationBanner_meetingRecording @arguments(public: $public)
			}
		`,
		meetingRecordingKey,
	);
	const comments = meetingRecording.comments;

	const isPublic = useIsPublic();
	const isXWC = meetingRecording.ownerAccount.__typename === "ExternalAccount";
	const isGuest = window.INITIAL_STATE.user?.type === "GUEST";

	const viewer = useFragment(
		graphql`
			fragment TranscriptPanel_Transcript_viewer on Viewer {
				account {
					allowAiFeatures
					allowMeetingRecordingRedaction
					billingAccount {
						hasAccessToRedaction: hasAccessToFeature(feature: redaction)
						hasAccessToRecapComments: hasAccessToFeature(feature: recap_comments)
						hasAccessToPrivateRecapComments: hasAccessToFeature(feature: recap_private_comments)
					}
				}
			}
		`,
		isPublic ? null : viewerKey,
	);

	const { analyticsFingerprint } = React.useContext(BillingContext);
	const [activeOverride, setActiveOverride] = React.useState<number | null>(null);
	const [searchValue, setSearchValue] = React.useState("");
	const [searchUsedValue, setSearchUsedValue] = React.useState(false);
	const debouncedSearchValue = useDebouncedValue(searchValue, 150);
	const hasSearchQueryRef = React.useRef(!!debouncedSearchValue);
	hasSearchQueryRef.current = !!debouncedSearchValue;

	const appContext = React.useContext(AppContext);
	const isUserAdmin = appContext.user?.internalAttributes?.isAccountAdmin ?? false;
	// UX Simplification
	const uxSimplificationEnabled = appContext.user?.uxSimplificationEnabled;

	const keyEventsMeetingRecording = useFragment<Timeline_meetingKeyEvents$key>(keyEventsFragment, meetingRecording);
	const keyEvents = keyEventsMeetingRecording?.meetingKeyEvents;

	const speakerNameToUserMapping = useSpeakerNameToUserMapping(meetingRecording);

	const transcriptWithKeyEvents = React.useMemo(() => {
		if (!transcript) return [];

		if (!keyEvents) return transcript;
		const transcriptWithKeyEvents: SpeakerTranscriptSegment[] = [];
		const allKeyEvents = keyEvents;
		transcript.forEach((segment, i) => {
			const speechSegment = { ...segment, segmentType: TranscriptSegmentType.speech };
			const segmentStart = speechSegment.words[0].start_timestamp;
			const segmentEnd = speechSegment.words[speechSegment.words.length - 1].end_timestamp;

			if (i == 0) {
				// Events before first transcript speech segment
				const keyEventsBeforeSegment =
					combineAndConvertKeyEvents(
						allKeyEvents.filter(event => segmentEnd >= event.timeFromRecordingStart),
						appContext.user?.fullName,
					) ?? [];
				transcriptWithKeyEvents.push(...keyEventsBeforeSegment, speechSegment);

				// If the first segment is also the last segment, we need to find events after
				if (transcript.length === 1) {
					const keyEventsAfterSegment =
						combineAndConvertKeyEvents(
							allKeyEvents.filter(event => segmentEnd < event.timeFromRecordingStart),
							appContext.user?.fullName,
						) ?? [];
					transcriptWithKeyEvents.push(...keyEventsAfterSegment);
				}
			} else {
				// Events in between all other segments
				const prevSegment = transcript[i - 1];
				const prevSegmentEnd = prevSegment.words[prevSegment.words.length - 1].end_timestamp;
				const keyEventsBetweenSegments =
					combineAndConvertKeyEvents(
						allKeyEvents.filter(
							event => prevSegmentEnd <= event.timeFromRecordingStart && event.timeFromRecordingStart <= segmentStart,
						),
						appContext.user?.fullName,
					) ?? [];
				const keyEventsWithinSegment = combineAndConvertKeyEvents(
					allKeyEvents.filter(
						event => segmentStart < event.timeFromRecordingStart && event.timeFromRecordingStart < segmentEnd,
					),
					appContext.user?.fullName,
				);
				transcriptWithKeyEvents.push(...keyEventsBetweenSegments, speechSegment, ...keyEventsWithinSegment);
			}

			if (i == transcript.length - 1) {
				// Events after the last segment
				const keyEventsAfterSegment =
					combineAndConvertKeyEvents(
						allKeyEvents.filter(event => segmentEnd < event.timeFromRecordingStart),
						appContext.user?.fullName,
					) ?? [];
				transcriptWithKeyEvents.push(...keyEventsAfterSegment);
			}
		});
		return transcriptWithKeyEvents;
	}, [appContext.user?.fullName, keyEvents, transcript]);

	const transcriptText = useMemo(
		() =>
			transcriptWithKeyEvents.map(
				segment => `${segment.words.map(word => word.text).join(" ")} ${getSegmentSpeaker(segment)}`,
			),
		[transcriptWithKeyEvents],
	);

	const transcriptHasNonLatinCharacters = useMemo(() => {
		// eslint-disable-next-line no-control-regex
		return transcriptText.some(segment => /[^\u0000-\u007F]/u.test(segment));
	}, [transcriptText]);

	const ufuzzy = useMemo(() => {
		// For more explanation of these options check here:
		// https://github.com/leeoniya/uFuzzy/blob/553f8fa1fc6941e086fdae192609a6e2315b2b7a/dist/uFuzzy.d.ts#L100-L155
		const UNICODE_OPTIONS = {
			unicode: true,
			interSplit: "[^\\p{L}\\d']+",
			intraSplit: "\\p{Ll}\\p{Lu}",
			intraBound: "\\p{L}\\d|\\d\\p{L}|\\p{Ll}\\p{Lu}",
			intraChars: "[\\p{L}\\d']",
			intraContr: "'\\p{L}{1,2}\\b",
		};

		return new uFuzzy({
			// Only enable unicode options if the transcript has non-latin characters (e.g. Chinese, Japanese, French etc.)
			...(transcriptHasNonLatinCharacters ? UNICODE_OPTIONS : {}),
			// Expect the search term to either be at the start of the word or at some
			// alphanumeric boundary, going from number to letter or lower to upper case.
			interLft: 1,
			// Tolerate some typos
			intraMode: 1,
			intraIns: 1,
			intraSub: 1,
			intraTrn: 1,
			intraDel: 1,
			// Keep in chronological order
			sort: info => [...info.idx.keys()],
		});
	}, [transcriptHasNonLatinCharacters]);

	const onSearchValueChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	}, []);

	const clearSearch = React.useCallback(() => setSearchValue(""), []);

	const onSegmentClick = React.useCallback(
		(segment: SpeakerTranscriptSegment) => {
			setActiveOverride(segment.words[0].start_timestamp);
			setScrollSynced(true);
			parentOnSegmentClick(segment);
		},
		[parentOnSegmentClick],
	);

	// NOTE: we're using the non-debounced one here, because we want clearing the
	// search to take effect immediately.
	const searchBecameEmpty = !!debouncedSearchValue && !searchValue;
	const matchedTranscriptSegments: readonly (readonly [
		segment: SpeakerTranscriptSegment,
		transcriptIndex: number,
		matches: number[] | undefined,
	])[] = React.useMemo(() => {
		if (searchBecameEmpty || !debouncedSearchValue)
			return transcriptWithKeyEvents.map((segment, i) => [segment, i, undefined]);
		const indexes = ufuzzy.filter(transcriptText, debouncedSearchValue);
		if (!indexes) return [];
		const info = ufuzzy.info(indexes, transcriptText, debouncedSearchValue);
		const order = ufuzzy.sort(info, transcriptText, debouncedSearchValue);
		setSearchUsedValue(true);
		return order.map(i => [transcriptWithKeyEvents[info.idx[i]], info.idx[i], info.ranges[i]] as const);
	}, [searchBecameEmpty, debouncedSearchValue, transcriptWithKeyEvents, ufuzzy, transcriptText]);

	const transcriptSegmentRefs = React.useRef(new Map<number, TranscriptSegmentRef>());
	const [scrollSynced, setScrollSynced] = React.useState(true);
	const prevAnimationRef = React.useRef<{ cancel(): void } | null>(null);

	React.useImperativeHandle(ref, () => ({
		// This will be debounced to 200ms since the animation is a 200ms animation
		handleTimeUpdate: debounce((currentTime: number) => {
			// Finds the first segment with a start timestamp greater or equal than the given timestamp
			const activeTranscriptSegment = matchedTranscriptSegments.find(([segment, transcriptIndex]) =>
				activeOverride !== null
					? activeOverride === segment.words[0].start_timestamp
					: (transcriptIndex === 0 && currentTime < segment.words[0].start_timestamp) ||
						(currentTime >= segment.words[0].start_timestamp &&
							(transcriptIndex + 1 === transcriptWithKeyEvents.length ||
								currentTime < transcriptWithKeyEvents[transcriptIndex + 1].words[0].start_timestamp)),
			)?.[0];

			prevAnimationRef.current?.cancel();
			const startTs = activeTranscriptSegment?.words[0].start_timestamp;
			if (startTs == null || !scrollSynced || hasSearchQueryRef.current) return;
			const animation = transcriptSegmentRefs.current.get(startTs)?.scrollIntoView("center");
			if (animation) prevAnimationRef.current = animation;
			for (const [ts, ref] of transcriptSegmentRefs.current) {
				ref.setActive(ts === startTs);
			}

			setActiveOverride(startTimestamp =>
				startTimestamp === null ? null : currentTime > startTimestamp ? null : startTimestamp,
			);
		}, 200),
		closeCommentsPanel() {
			setSelectedCommentTimestampStart(null);
			setSelectedCommentTimestampEnd(null);
		},
	}));

	const toggleAutoScroll = React.useCallback(() => setScrollSynced(prev => !prev), []);
	const onScroll = React.useCallback(() => {
		if (autoScrolling) {
			autoScrolling = false;
			return;
		}
		if (hasSearchQueryRef.current) return;
		// Rerendering during the wheel or touchmove feels janky, so do the update
		// after processing the event is done.
		prevAnimationRef.current?.cancel();
		setTimeout(() => setScrollSynced(false), 0);
	}, []);

	const makeRefHandler = React.useMemo(
		() =>
			memoize((segment: SpeakerTranscriptSegment) => (ref: TranscriptSegmentRef) => {
				if (ref) {
					transcriptSegmentRefs.current.set(segment.words[0].start_timestamp, ref);
				}
			}),
		[],
	);

	React.useEffect(() => {
		if (searchUsedValue) {
			// Only track the first search in the modal
			if (meetingRecording.__typename === "MeetingRecording") {
				dispatchAnalyticsTrack("[AI Copilot] Transcript Searched", {
					fingerprint: analyticsFingerprint,
					note_id: note?.id, // eslint-disable-line camelcase
				});
			} else if (meetingRecording.__typename === "MeetingRecordingClip") {
				dispatchAnalyticsTrack("[AI Copilot] Clip Transcript Searched", {
					fingerprint: analyticsFingerprint,
					clip_id: meetingRecording.id, // eslint-disable-line camelcase
				});
			}
		}
	}, [meetingRecording, searchUsedValue, note, analyticsFingerprint]);

	const planHasRedaction = viewer?.account.billingAccount.hasAccessToRedaction ?? false;
	const allowAiFeatures = viewer?.account.allowAiFeatures ?? false;
	const allowMeetingRecordingRedaction = viewer?.account.allowMeetingRecordingRedaction ?? false;
	const hasAccessToRecapComments = viewer?.account.billingAccount.hasAccessToRecapComments ?? false;
	const hasAccessToPrivateRecapComments = viewer?.account.billingAccount.hasAccessToPrivateRecapComments ?? false;
	const redactionIsVisible =
		allowAiFeatures &&
		allowMeetingRecordingRedaction &&
		!!note?.viewerCanEdit &&
		!isXWC &&
		!isGuest &&
		!isPublic &&
		meetingRecording.__typename === "MeetingRecording";
	const regenerationBannerIsVisible =
		allowAiFeatures &&
		!!note?.viewerCanEdit &&
		!note?.isLocked &&
		!isXWC &&
		!isGuest &&
		!isPublic &&
		meetingRecording.__typename === "MeetingRecording" &&
		meetingRecording.showLowLanguageConfidenceRegenerationBanner === true;
	const assertRedactionIsVisible = React.useCallback(() => {
		if (!allowAiFeatures) throw new Error("Workspace has disabled AI features");
		if (!allowMeetingRecordingRedaction) throw new Error("Workspace has disabled meeting recording redaction");
		if (!note?.viewerCanEdit) throw new Error("User cannot edit this note");
		if (isXWC) throw new Error("External users cannot redact");
		if (isGuest) throw new Error("Guest users cannot redact");
		if (isPublic) throw new Error("Redaction is not allowed in public view");
		if (meetingRecording.__typename !== "MeetingRecording")
			throw new Error("Redaction is only allowed on recordings, not clips");
	}, [
		allowAiFeatures,
		allowMeetingRecordingRedaction,
		isGuest,
		isPublic,
		isXWC,
		meetingRecording.__typename,
		note?.viewerCanEdit,
	]);

	const analyticsAttributes = React.useMemo(
		() => ({
			/* eslint-disable camelcase */
			fingerprint: analyticsFingerprint,
			meeting_recording_id: meetingRecording.id,
			meeting_recording_summary_id: meetingRecording.activeSummary?.id,
			/* eslint-enable camelcase */
		}),
		[analyticsFingerprint, meetingRecording.activeSummary?.id, meetingRecording.id],
	);

	const [selectedCommentTimestampStart, setSelectedCommentTimestampStart] = React.useState<number | null>(null);
	const [selectedCommentTimestampEnd, setSelectedCommentTimestampEnd] = React.useState<number | null>(null);
	const closeCommentsPanel = React.useCallback(() => {
		setSelectedCommentTimestampStart(null);
		setSelectedCommentTimestampEnd(null);
	}, []);
	const recapCommentsFeatureIsEnabled = useFeatureIsOn("recap-comments");
	const openCommentPanel = React.useCallback(
		(start: number, end: number) => {
			const existingComment = comments?.find(comment =>
				isSameFloatNumber(Number.parseFloat(comment.startTimestamp), start, TIME_COMPARISON_TOLERANCE),
			);

			if (!hasAccessToRecapComments && !existingComment) {
				showFeatureModal("recap_comments");
				return;
			}

			setSelectedCommentTimestampStart(start);
			setSelectedCommentTimestampEnd(end);

			transcriptSegmentRefs.current.get(start)?.scrollIntoView("top");
			if (pauseVideo) pauseVideo();
		},
		[pauseVideo, hasAccessToRecapComments, comments],
	);

	const { timestampStart, timestampEnd } = React.useContext(TranscriptModalContext);
	const location = useLocation();

	React.useEffect(() => {
		if (timestampStart && timestampEnd) {
			setSelectedCommentTimestampStart(timestampStart);
			setSelectedCommentTimestampEnd(timestampEnd);
		} else {
			const ts = getTimestampFromLocation(location, "ts");
			const te = getTimestampFromLocation(location, "te");
			if (ts && te) {
				setSelectedCommentTimestampStart(ts);
				// we don't want to open the popover component on public
				// because commentQuery returns 403 which crashes the page
				// and enforces a redirect to login page
				if (!isPublic) {
					setSelectedCommentTimestampEnd(te);
				}
			}
		}
	}, [isPublic, location, timestampStart, timestampEnd]);

	const inRedactionMode = mode === "redacting";
	const [redactingSpans, setRedactingSpans] = React.useState<ReadonlyArray<RedactingSpan>>([]);
	const [blockSelectionStart, setBlockSelectionStart] = React.useState<number | null>(null);
	const trackStartedRedaction = React.useCallback(
		(location: string) => {
			dispatchAnalyticsTrack("[AI Copilot] Started Redaction", {
				...analyticsAttributes,
				// eslint-disable-next-line camelcase
				ux_location: location,
			});
		},
		[analyticsAttributes],
	);
	const enterRedactionMode = React.useCallback(
		(analyticsLocation: string) => {
			assertRedactionIsVisible();
			if (!planHasRedaction) {
				showFeatureModal("redaction");
				return;
			}
			closeCommentsPanel();
			setSearchValue("");
			setMode?.("redacting");
			setScrollSynced(false);
			setRedactingSpans([]);
			setBlockSelectionStart(null);
			trackStartedRedaction(analyticsLocation);
		},
		[assertRedactionIsVisible, planHasRedaction, setMode, trackStartedRedaction, closeCommentsPanel],
	);
	const getEnterRedactionMode = React.useMemo(
		() =>
			memoize((analyticsLocation: string) => () => {
				enterRedactionMode(analyticsLocation);
			}),
		[enterRedactionMode],
	);
	const addRedactingSpan = React.useCallback(
		(span: RedactingSpan) => {
			assertRedactionIsVisible();
			// Detect overlapping spans and combine them with existing
			// NB: This is definitely doable in log(n) time, but the number of spans is likely
			// to be small and I'm in a hurry.
			setRedactingSpans(prev => {
				return [...prev, span]
					.sort((a, b) => a[0] - b[0])
					.reduce<RedactingSpan[]>((acc, [start, end]) => {
						const last = acc[acc.length - 1];
						if (last && last[1] >= start) {
							last[1] = Math.max(last[1], end);
						} else {
							acc.push([start, end]);
						}
						return acc;
					}, []);
			});
		},
		[assertRedactionIsVisible],
	);
	const exitRedactionMode = React.useCallback(async () => {
		setSearchValue("");
		setMode?.("default");
		setRedactingSpans([]);
		setBlockSelectionStart(null);
		window.getSelection()?.removeAllRanges();
	}, [setMode]);
	const confirmAndCancelRedaction = React.useCallback(async () => {
		if (redactingSpans.length > 0) {
			const confirm = await confirmDiscardRedactions();
			if (!confirm) return;
		}
		dispatchAnalyticsTrack("[AI Copilot] Cancelled Redaction", analyticsAttributes);
		exitRedactionMode();
	}, [analyticsAttributes, exitRedactionMode, redactingSpans.length]);
	const modals = useModals();
	const saveRedaction = React.useCallback(async () => {
		assertRedactionIsVisible();
		const confirmationResult = await modals.open<[boolean, boolean]>(({ close }) => (
			<ConfirmRedactionModal onClose={close} canRegenerateSummary={meetingRecording.canRegenerateSummary ?? false} />
		));
		if (!confirmationResult) return;
		const [confirm, regenerateOnFinish] = confirmationResult;
		if (!confirm) return;
		try {
			await commitMutationPromise(environment, {
				mutation: redactMutation,
				variables: {
					meetingRecordingId: meetingRecording.id,
					redactingSpans: redactingSpans,
					regenerateOnFinish,
				},
			});
			dispatchAnalyticsTrack("[AI Copilot] Completed Redaction", analyticsAttributes);
		} catch (e) {
			const userFacingError = getFirstUserFacingError(e) ?? "Something went wrong, please try again later";
			toast.error(userFacingError, { title: "Redaction failed" });
			return;
		}
		exitRedactionMode();
	}, [
		analyticsAttributes,
		assertRedactionIsVisible,
		exitRedactionMode,
		meetingRecording.canRegenerateSummary,
		meetingRecording.id,
		modals,
		redactingSpans,
	]);

	const [parentRef, selection, isOpen, FloatingElement] = useFloatingElement<HTMLOListElement>();

	const redactSelectedText = React.useCallback(() => {
		if (!selection) throw new Error("No selection, can't redact it");
		const startRange = selection.getRangeAt(0);
		const endRange = selection.getRangeAt(selection.rangeCount - 1);
		const start = getTimestampFromDOMPosition(startRange.startContainer, startRange.startOffset, transcript, "start");
		const end = getTimestampFromDOMPosition(endRange.endContainer, endRange.endOffset, transcript, "end");
		if (start === null || end === null) {
			throw new Error("Failed to get timestamps from selection");
		}
		const span: RedactingSpan = [start, end];
		if (!inRedactionMode) {
			enterRedactionMode("transcript_floating_redact_button");
		}
		addRedactingSpan(span);
		window.getSelection()?.removeAllRanges();
	}, [addRedactingSpan, enterRedactionMode, inRedactionMode, selection, transcript]);

	/** This is the text content of the selection, and is used here
	 * as a dependency to ensure that these memoized values are
	 * recalculated when the selection changes, because the selection
	 * object itself gets mutated, so we can't rely on it to trigger
	 * a re-evaluation. */
	const selectedText = selection?.toString();

	const selectedStart = React.useMemo(() => {
		if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
		const range = selection.getRangeAt(0);
		return getTimestampFromDOMPosition(range.startContainer, range.startOffset, transcript, "start");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection, transcript, selectedText]);

	const selectedEnd = React.useMemo(() => {
		if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
		const range = selection.getRangeAt(selection.rangeCount - 1);
		return getTimestampFromDOMPosition(range.endContainer, range.endOffset, transcript, "end");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selection, transcript, selectedText]);

	const startBlockSelection = React.useCallback(
		(segment: SpeakerTranscriptSegment) => {
			if (!selection) {
				setBlockSelectionStart(segment.words[0].start_timestamp);
			} else if (selectedEnd !== null) {
				const newSelection = window.getSelection();
				if (!newSelection) throw new Error("Selection is null"); // Theoretically this shouldn't happen
				if (!parentRef.current) throw new Error("Parent ref is null");

				const lastRange = selection.getRangeAt(selection.rangeCount - 1);
				const newEnd = [lastRange.endContainer, lastRange.endOffset] as const;
				newSelection.removeAllRanges();
				const newStart = getDOMPositionFromTimestamp(
					parentRef.current,
					segment.words[0].start_timestamp,
					transcript,
					"start",
					segment,
				);
				// TODO: When setting selection programmatically like this, Firefox includes things
				// with `user-select: none`, while Chrome doesn't. Haven't tested Safari yet. It'd
				// be nice if we specifically excluded those elements from the selection.
				newSelection.setBaseAndExtent(newStart[0], newStart[1], newEnd[0], newEnd[1]);
				setBlockSelectionStart(null);
			} else {
				throw new Error("Invalid state?");
			}
		},
		[parentRef, selectedEnd, selection, transcript],
	);

	const setBlockSelectionEnd = React.useCallback(
		(segment: SpeakerTranscriptSegment) => {
			const newStartTimestamp = selectedStart ?? blockSelectionStart;
			if (newStartTimestamp === null) throw new Error("Must have a start selected before end");
			const newSelection = window.getSelection();
			if (newSelection === null) throw new Error("Selection is null"); // Theoretically this shouldn't happen
			if (!parentRef.current) throw new Error("Parent ref is null");
			newSelection.removeAllRanges();
			const newStart = getDOMPositionFromTimestamp(parentRef.current, newStartTimestamp, transcript, "start");
			const newEnd = getDOMPositionFromTimestamp(
				parentRef.current,
				segment.words[segment.words.length - 1].end_timestamp,
				transcript,
				"end",
				segment,
			);
			newSelection.setBaseAndExtent(newStart[0], newStart[1], newEnd[0], newEnd[1]);
			setBlockSelectionStart(null);
		},
		[blockSelectionStart, parentRef, selectedStart, transcript],
	);

	const redactionsBySegment: Map<number, readonly RedactingSpan[]> = React.useMemo(() => {
		/** Because redacted spans are non-overlapping and sorted, this is
		 * the earliest possible segment index that the next redaction span
		 * could start in. This lets us reduce search time. */
		let lowestPossibleIncludedSegment = 0;
		const redactionsBySegment = new Map<number, RedactingSpan[]>();
		for (const redactingSpan of redactingSpans) {
			const [start, end] = redactingSpan;
			/* "cursors" like prosemirror positions, e.g.:
			 *           `[ ╷ 'a', ╷ 'b', ╷ 'c', ╷ 'd' ╷ ]`
			 *              │      │      │      │     │
			 *  cursor:     └0     └1     └2     └3    └4 */
			const startSegmentCursor = bisectSegmentList(transcript, start, lowestPossibleIncludedSegment, undefined, "left"),
				endSegmentCursor = bisectSegmentList(transcript, end, startSegmentCursor, undefined, "right");

			for (let i = startSegmentCursor; i < endSegmentCursor; i++) {
				const segment = transcript[i];
				const startTimestamp = segment.words[0]?.start_timestamp;
				if (startTimestamp == undefined) continue;
				if (!redactionsBySegment.has(startTimestamp)) {
					redactionsBySegment.set(startTimestamp, []);
				}
				redactionsBySegment.get(startTimestamp)?.push(redactingSpan);
			}
			lowestPossibleIncludedSegment = endSegmentCursor - 1;
		}
		return redactionsBySegment;
	}, [redactingSpans, transcript]);

	const openRelabelSpeakersModal = React.useCallback(() => {
		openRelabelMultipleSpeakersModal(meetingRecording, analyticsFingerprint);
	}, [analyticsFingerprint, meetingRecording]);

	const onCopyTranscriptText = React.useCallback(() => {
		const transcriptText = formatTranscriptAsText(transcriptWithKeyEvents, videoDuration);
		copyToClipboard(transcriptText).then(() => {
			dispatchAnalyticsTrack("[AI Copilot] Transcript Text Copied", {
				fingerprint: analyticsFingerprint,
			});
		});
	}, [transcriptWithKeyEvents, videoDuration, analyticsFingerprint]);

	const isRedactingMode = mode === "redacting";

	const commentsPopoverRef = React.useRef<HTMLDivElement>(null);

	// Use useEffect to scroll the popover into view when selectedCommentTimestampStart/End changes
	React.useEffect(() => {
		if (selectedCommentTimestampStart !== null && selectedCommentTimestampEnd !== null) {
			// Short timeout to ensure the popover is mounted in the DOM
			const timer = setTimeout(() => {
				if (commentsPopoverRef.current) {
					commentsPopoverRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
				}
			}, 200);
			return () => clearTimeout(timer);
		}
		return noop;
	}, [selectedCommentTimestampStart, selectedCommentTimestampEnd]);

	return (
		<article
			className={cx(styles.transcriptContainer, {
				[styles.fromNote]: fromNote,
				[styles.simplifiedUxContainer]: uxSimplificationEnabled,
			})}
			data-type={fromNote ? "note-content" : undefined}
			data-tour-tooltip-target={uxSimplificationEnabled ? `toggle-group-${TRANSCRIPT_TAB.value}` : undefined}
		>
			{!transcriptDeletionReason &&
				(!isRedactingMode ? (
					<header className={cx(styles.transcriptHeader, styles.noTitle)}>
						{note?.id && regenerationBannerIsVisible && !uxSimplificationEnabled && (
							<LowLanguageConfidenceRegenerationBanner
								className={styles.unlabeledSpeakersBannerMargin}
								meetingRecording={meetingRecording}
								noteId={note.id}
								trackLowLanguageConfidenceScoreBannerShown={trackLowLanguageConfidenceScoreBannerShown}
							/>
						)}
						<UnlabeledSpeakersWarning
							className={styles.unlabeledSpeakersBannerMargin}
							meetingRecording={meetingRecording}
							onLabelSpeakersClick={openRelabelSpeakersModal}
						/>

						<div className={styles.searchAndButtonsContainer}>
							<TextField
								value={searchValue}
								onChange={onSearchValueChange}
								type="text"
								className={styles.transcriptSearchInput}
								wrapperClassname={styles.transcriptSearchWrapper}
								placeholder="Search Transcript…"
								icon={<FontAwesomeIcon icon={faSearch} className={styles.transcriptSearchInputIcon} />}
								withCross
								onClear={clearSearch}
							/>
							{transcriptWithKeyEvents.length > 0 && (
								<Tooltip content={"Toggle autoscroll"}>
									<IconButton
										variant="tertiary"
										className={cx(styles.autoScrollButton, { [styles.active]: scrollSynced })}
										icon={faArrowsFromDottedLine}
										onClick={toggleAutoScroll}
									/>
								</Tooltip>
							)}
							{(setMode && redactionIsVisible) || transcriptWithKeyEvents.length > 0 ? (
								<Menu>
									<Menu.Trigger>
										<IconButton variant="tertiary" className={styles.redactMenuTrigger} icon={faEllipsisV} />
									</Menu.Trigger>
									<Menu.Content align="end">
										{transcriptWithKeyEvents.length > 0 && (
											<Menu.Item icon={faCopy} onSelect={onCopyTranscriptText}>
												Copy transcript
											</Menu.Item>
										)}
										{setMode && redactionIsVisible && (
											<>
												{transcriptWithKeyEvents.length > 0 && <Menu.Separator />}
												<Menu.Item icon={faDeleteRightLight} onSelect={getEnterRedactionMode("transcript_kebab_menu")}>
													Redact…
													{!planHasRedaction && (
														<>
															&ensp;
															<UpgradeIcon />
														</>
													)}
												</Menu.Item>
											</>
										)}
									</Menu.Content>
								</Menu>
							) : null}
						</div>

						{note?.id && selectedCommentTimestampStart !== null && selectedCommentTimestampEnd !== null && (
							<TranscriptCommentsPopover
								ref={commentsPopoverRef}
								noteId={note.id}
								organizerId={note.event?.organizer?.id}
								recordingId={meetingRecording.id}
								selectedCommentTimestampStart={selectedCommentTimestampStart}
								selectedCommentTimestampEnd={selectedCommentTimestampEnd}
								closeCommentPanel={closeCommentsPanel}
								openCommentPanel={openCommentPanel}
								noteUsers={note.noteuserSet.map(nu => nu.user)}
								hasAccessToRecapComments={hasAccessToRecapComments}
								hasAccessToPrivateRecapComments={hasAccessToPrivateRecapComments}
								narrowViewport={narrowViewport}
								uxSimplificationEnabled={uxSimplificationEnabled}
							/>
						)}
					</header>
				) : (
					<RedactingTranscriptHeader
						exitRedactionMode={confirmAndCancelRedaction}
						readyToSave={redactingSpans.length > 0}
						saveRedaction={saveRedaction}
					/>
				))}
			<main
				className={cx(styles.transcriptBody, {
					[styles.transcriptBodyDeleted]: !!transcriptDeletionReason,
					[styles.spaceForAskCopilotButton]: makeSpaceForAskCopilotButton,
				})}
				onScroll={onScroll}
			>
				{transcriptDeletionReason ? (
					<div className={styles.transcriptDeletedContainer}>
						{transcriptDeletionReason === "AUTO" ? (
							<div className={styles.transcriptDeletedParagraph}>
								This transcript was automatically deleted based on{" "}
								{isPublic ? (
									<>the workspace settings</>
								) : isUserAdmin ? (
									<>
										your{" "}
										<Link to="/account/copilot/settings/" onClick={closeModal}>
											workspace settings
										</Link>
									</>
								) : (
									<>your workspace settings</>
								)}
								.
							</div>
						) : (
							<div className={styles.transcriptDeletedParagraph}>This transcript was deleted.</div>
						)}
					</div>
				) : transcriptWithKeyEvents.length ? (
					<ol className={styles.transcriptSegmentList} ref={parentRef}>
						{matchedTranscriptSegments.map(([segment, , matches], i) => {
							const startTimestamp = segment.words[0].start_timestamp;
							const commentData = comments?.filter(c => isSameFloatNumber(Number(c.startTimestamp), startTimestamp));

							return (
								segment && (
									<TranscriptSegment
										note={note}
										ref={makeRefHandler(segment)}
										key={`${startTimestamp}-${segment.segmentType}`}
										meetingRecording={meetingRecording}
										segment={segment}
										onClick={onSegmentClick}
										onClickRedactionStartMarker={startBlockSelection}
										onClickRedactionEndMarker={setBlockSelectionEnd}
										videoDuration={videoDuration}
										searchMatches={matches}
										speakerNameToUserMapping={speakerNameToUserMapping}
										redactionIsVisible={redactionIsVisible}
										planHasRedaction={planHasRedaction}
										activeRedactions={inRedactionMode ? (redactionsBySegment.get(startTimestamp) ?? []) : null}
										enterRedactionMode={enterRedactionMode}
										addRedactingSpan={addRedactingSpan}
										currentSpanStart={selectedStart ?? blockSelectionStart}
										currentSpanEnd={selectedEnd}
										sameSpeakerAsPreviousSegment={
											i !== 0 &&
											segment.segmentType === TranscriptSegmentType.speech &&
											matchedTranscriptSegments[i - 1][0].segmentType === TranscriptSegmentType.speech &&
											matchedTranscriptSegments[i - 1][0].speaker === segment.speaker
										}
										isClip={isClip}
										openCommentPanel={recapCommentsFeatureIsEnabled ? openCommentPanel : undefined}
										selectedCommentTimestampStart={selectedCommentTimestampStart}
										selectedCommentTimestampEnd={selectedCommentTimestampEnd}
										commentData={commentData}
										isRedactionOpen={isOpen}
										isRedactionInProgress={isRedactingMode}
										hasAccessToRecapComments={hasAccessToRecapComments}
									/>
								)
							);
						})}
						{isOpen &&
							redactionIsVisible &&
							selectedStart !== null &&
							selectedEnd !== null &&
							React.cloneElement(
								FloatingElement,
								undefined,
								<div
									style={{
										background: "var(--flyoutBackgroundColor)",
										borderRadius: 8,
										boxShadow: "var(--flyoutBoxShadow)",
										padding: "4px",
									}}
								>
									<Button
										size="slim"
										variant="tertiary"
										onClick={redactSelectedText}
										style={{
											padding: "2px 6px",
										}}
									>
										{planHasRedaction ? (
											<>
												<FontAwesomeIcon icon={faDeleteRightRegular} />
											</>
										) : (
											<>
												<UpgradeIcon />
											</>
										)}
										{THREE_PER_EM_SPACE}
										{inRedactionMode ? "Add to redaction" : "Redact…"}
									</Button>
								</div>,
							)}
					</ol>
				) : (
					<article className={styles.transcriptEmptyStateContainer}>
						<header className={styles.transcriptEmptyStateHeader}>
							<p className={styles.transcriptEmptyStateEyes}>👀</p>
							<h1 className={styles.transcriptEmptyStateHeading}>Nothing to see here…</h1>
						</header>
						<main className={styles.transcriptEmptyStateBody}>
							<p className={styles.transcriptEmptyStateText}>
								Your transcript was empty because nothing was said in this meeting.
							</p>
						</main>
					</article>
				)}
			</main>
		</article>
	);
});
